// AdminDailyLog — admin sub-view, extracted verbatim from App.js.
import { useState, useEffect, useMemo, useRef } from "react";
import { C } from "../lib/theme";
import { posc } from "../lib/format";
import { Card, StatCard, EmptyState, SectionHdr, InfoTip } from "../components/ui";
import { CsvImportExport } from "../components/CsvImportExport";
import { pick } from "../lib/csv";
import { calcDaily, applyPeriodAdjustments } from "../lib/calc";
import { insertDailyLog, deleteDailyLog, updateDailyLog, upsertDailyLog } from "../data/dailyLogs";
import { insertCpcAllocation, updateCpcAllocation, deleteCpcAllocation } from "../data/cpcAllocations";
import { insertCoFundAllocation, updateCoFundAllocation, deleteCoFundAllocation } from "../data/coFundAllocations";
import { effNet, coFundAmount, coFundBreakdown, coFundRatesFor, validateCoFundInput, findCoFundOverlap, pctInputToFraction, fractionToPctInput, sumCoFundByEntry, COFUND_PCT_MIN, COFUND_PCT_MAX } from "../lib/coFund";
import { cpcForPeriod } from "../lib/cpcAlloc";
import { openDatePicker, openPickerProps } from "../lib/datePicker";
import { filterHistory, rangeWindow } from "../lib/ranges";
import { useTableSortFilter, SortableTh } from "../lib/tableSort";
import { API_BASE } from "../lib/api";
import { getCurrentSession } from "../data/auth";
import { useT } from "../lib/i18n";
import { track } from "../lib/track";
import { isFedRow, isFedDayError, hasFedRowOnDay, rowDay } from "../lib/dailyLogFeed";

// CSV columns for the Daily Log. ID first so an exported file round-trips:
// re-importing it updates existing entries (matched by id) instead of duplicating.
const DAILY_CSV_HEADERS = ["ID", "Platform", "Date", "KOT", "Gross", "Net", "Campaign Fee"];
const EMPTY_COFUND_FORM = {channelId:"",dateFrom:"",dateTo:"",discountPct:"",cofundPct:""};

// A Supabase/Postgres row-level-security denial on a write (Postgres code 42501).
// It happens when the account has lost write access — e.g. the trial expired
// between page load and save, so the daily_logs INSERT/UPDATE WITH CHECK
// (has_active_access) now fails. We treat it specially so the user sees a
// "your trial ended — subscribe" prompt and gets routed to the paywall, instead
// of a raw Postgres error string.
// 046's fed-day refusal (NF001) is never an access problem, whatever words its
// message uses: read by code first, so a reworded trigger message that says
// "row-level" can't send a refused fed-day write to the paywall on any path.
function isAccessEndedError(error) {
  if (!error) return false;
  if (isFedDayError(error)) return false;
  const code = (error.code || "").toString();
  const msg  = (error.message || "").toLowerCase();
  return code === "42501" || msg.includes("row-level security") || msg.includes("violates row-level");
}

// InfoTip (portal-based ⓘ tooltip) now lives in ../components/ui — imported above.

export function AdminDailyLog({ws, cfg, userPlan, currency="GBP", chId, setWorkspaces, wsId, refreshWorkspaces, refreshAccount, range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, addOpen, setAddOpen, exportTick, dlTab, setDlTab, cpcAddTick, coFundAddTick}) {
  // i18n: alias to `tr` because a local `t` (period totals object) is used below.
  const { t: tr } = useT();
  // An access/RLS denial (Postgres 42501) on any daily-log/CPC write means the
  // trial/subscription lapsed. Refresh account status so the app routes to the
  // subscribe paywall, and signal the caller to skip the raw-error path. Used by
  // every write handler below (insert, edit, delete, CPC) so they behave
  // consistently instead of only the new-entry insert doing it.
  const accessRouted = (error) => { if (isAccessEndedError(error)) { refreshAccount?.(); return true; } return false; };
  // range/customFrom/customTo and the add-form open flag now come from AdminView
  // (shared with the pill-bar toolbar). Alias to the old local names so the rest
  // of the component is unchanged. The Daily log ⇄ CPC toggle (one table at a
  // time) is shown again; the tab STATE is lifted to App.js so the top toolbar's
  // +Add / Export buttons can switch with it.
  const adding = addOpen;
  const setAdding = setAddOpen;
  const tab = dlTab ?? "log"; // "log" | "cpc" | "cofund"
  const setTab = setDlTab || (()=>{});
  const [saving,setSaving]=useState(false);
  const [form,setForm]=useState({channelId:"",date:"",gross:"",net:"",campaignFee:"",kot:""});
  const [err,setErr]=useState("");
  // Inline row editing (#: modify rows). editingId = the row being edited;
  // editForm holds the in-progress values; editSaving guards double-submits.
  const [editingId,setEditingId]=useState(null);
  const [editForm,setEditForm]=useState({gross:"",net:"",campaignFee:"",kot:""});
  const [editSaving,setEditSaving]=useState(false);
  // CPC / campaign spend now lives in its own date-range allocations table
  // (cpc_allocations) instead of the daily-log row. The add form (below) creates
  // new entries; existing rows are edited INLINE in the table (cpcEditingId).
  const [cpcAdding,setCpcAdding]=useState(false);
  const [cpcForm,setCpcForm]=useState({channelId:"",dateFrom:"",dateTo:"",amount:""});
  const [cpcSaving,setCpcSaving]=useState(false);
  const [cpcErr,setCpcErr]=useState("");
  // Inline editing of an existing CPC allocation row.
  const [cpcEditingId,setCpcEditingId]=useState(null);
  const [cpcEditForm,setCpcEditForm]=useState({dateFrom:"",dateTo:"",amount:""});
  const [cpcEditSaving,setCpcEditSaving]=useState(false);
  // Platform contribution (co-funded discounts) — platform_cofunding date-range
  // entries, cloned from the CPC flow above. Percent inputs are UI strings in
  // WHOLE percent ("50", "45"); stored as fractions (lib/coFund.js).
  const [coFundAdding,setCoFundAdding]=useState(false);
  const [coFundForm,setCoFundForm]=useState(EMPTY_COFUND_FORM);
  const [coFundSaving,setCoFundSaving]=useState(false);
  const [coFundErr,setCoFundErr]=useState("");
  const [coFundEditingId,setCoFundEditingId]=useState(null);
  const [coFundEditForm,setCoFundEditForm]=useState({dateFrom:"",dateTo:"",discountPct:"",cofundPct:""});
  const [coFundEditSaving,setCoFundEditSaving]=useState(false);
  const sym = currency==="AED"?"AED ":currency==="USD"?"$":currency==="INR"?"₹":"£";
  const channels = ws?.channels ? Object.values(ws.channels) : [];
  // FIRST VALUE: a workspace with no entries at all gets a guided "log
  // yesterday" callout, and its first saved entry shows what the operator
  // actually kept after platform fees — the moment the product proves itself.
  const totalLogs = channels.reduce((n,c)=>n+((c.history||[]).length),0);
  const isFirstRun = totalLogs===0;
  const [firstResult,setFirstResult]=useState(null);
  const yesterdayYmd = () => { const d=new Date(Date.now()-86400000); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };

  // Daily Log is platform-specific — only show entries for selected channel
  const sourceHistory = useMemo(()=>{
    const channelLookup = ws?.channels || {};
    // Stamp each row with its channel's id, platform name and rates so the
    // Channel column resolves correctly — especially in the "All" view, where
    // rows come from many channels and must each carry their own platform.
    const enrichRow = (r, ch) => ({
      ...r,
      channelId:  r.channelId ?? ch?.id,
      platform:   r.platform  ?? ch?.platform,
      commission: r.commission ?? ch?.commission,
      cardFeePct: r.cardFeePct ?? ch?.cardFeePct,
    });
    if(chId && ws?.channels?.[chId]){
      const ch = ws.channels[chId];
      return (ch.history||[]).map(r=>enrichRow(r, ch));
    }
    // "All" — flatten every channel's history, stamping each row with its channel.
    const all = [];
    Object.values(channelLookup).forEach(ch => {
      (ch.history||[]).forEach(r => all.push(enrichRow(r, ch)));
    });
    return all;
  },[ws,chId]);
  const filterLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : tr("dailylog.filter.allPlatforms");

  // Initialize form channel when opening the "+ Add log entry" form.
  function openAddForm(){
    const defaultCh = chId || channels[0]?.id || "";
    if(channels.length>0) setForm({...form, channelId:defaultCh});
    setAdding(true); setErr("");
  }

  // The "+ Add log entry" button lives in the top toolbar (App.js) and simply
  // flips addOpen=true (after switching to the log tab). When it does, preset the
  // default channel so the form opens ready to use (mirrors openAddForm).
  // Seeded FALSE on purpose: onFirstWorkspaceCreated navigates here with
  // addOpen already true, so the page mounts with it set. Seeding the ref from
  // the prop would make the effect below see "no change" and skip presetting
  // the platform + yesterday's date — the guided first entry would open blank.
  const addOpenRef = useRef(false);
  useEffect(()=>{
    if(addOpen && !addOpenRef.current){
      const defaultCh = chId || channels[0]?.id || "";
      if(channels.length>0) setForm(f=>({...f, channelId:defaultCh, date: f.date || (isFirstRun ? yesterdayYmd() : "")}));
      setErr("");
    }
    addOpenRef.current = addOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[addOpen]);

  async function saveEntry(){
    // A day the integration feed covers takes no typed rows (migration 046's
    // trigger refuses them too). Checked first, so the user sees the real
    // reason before being asked for figures that can't be saved.
    if(form.channelId && form.date && hasFedRowOnDay(ws?.channels?.[form.channelId]?.history, form.date)){
      setErr(tr("dailylog.feed.dayLocked")); return;
    }
    if(!form.channelId||!form.date||!form.gross){setErr(tr("dailylog.err.dateGrossPlatformRequired"));return;}
    if(!form.kot||!form.kot.trim()){setErr(tr("dailylog.err.kotRequired"));return;}
    // KOT numbers must be UNIQUE per platform + per date (per workspace). Block a duplicate
    // before hitting the DB so the same ticket isn't logged twice. (Mobile enforces this too.)
    {
      const kotVal = form.kot.trim().toLowerCase();
      const dupe = (ws?.channels?.[form.channelId]?.history || []).some(r =>
        String(r.date) === String(form.date) &&
        String(r.kotNumber ?? "").trim().toLowerCase() === kotVal
      );
      if(dupe){ setErr(tr("dailylog.err.kotDuplicate")); return; }
    }
    // Net is sales after discount — it can never exceed gross. Reject bad data here.
    {
      const gCheck = parseFloat(form.gross)||0;
      const nCheck = form.net !== "" && form.net != null ? parseFloat(form.net) : gCheck;
      if (!isNaN(nCheck) && gCheck > 0 && nCheck > gCheck) {
        setErr(`${tr("dailylog.err.netAboveGrossPrefix")}£${nCheck.toFixed(2)}${tr("dailylog.err.netAboveGrossMid")}£${gCheck.toFixed(2)}${tr("dailylog.err.netAboveGrossSuffix")}`);
        return;
      }
    }
    // Date picker returns YYYY-MM-DD format directly, which is what Supabase needs
    const logDate = form.date;
    setSaving(true);
    try {
      const ch = ws.channels[form.channelId];
      const gross = parseFloat(form.gross)||0;
      const net = parseFloat(form.net) || gross;
      const campFee = parseFloat(form.campaignFee)||0;
      const commRate = ch?.commission || 0;
      const cardRate = ch?.cardFeePct || 0;
      // Platform co-funding for this date (per ROW; the same helper db.js uses).
      const coFundRates = coFundRatesFor(ch?.coFundAllocations || [], logDate, form.channelId);
      const cf = coFundBreakdown(gross, net, coFundRates);
      const calc = calcDaily(gross, cf.effectiveNet, 0, {
        aggCommission: commRate,
        cardFee: cardRate,
        vatRate: ws?.vatRate ?? cfg.vatRate,
        cogsPct: ws?.cogsPct ?? cfg.cogsPct,
      });
      const { data, error } = await insertDailyLog({
        workspace_id: ws.id,
        channel_id: form.channelId,
        platform: ch?.platform,
        log_date: logDate,
        kot_number: form.kot.trim() || null,
        gross,
        net_sales: net,
        campaign_fee: campFee,
        discount_type: "none",
        net_receivable: calc?.netReceivable||null,
        vat: calc?.vat||null,
        earning_after_vat: calc?.earningAfterVat||null,
        cogs: calc?.cogs||null,
        true_profit: calc?.trueProfit||null,
      });
      if(error) {
        // Trial/subscription lapsed between load and save → friendly prompt +
        // refresh status so the app routes to the subscribe paywall, rather than
        // surfacing the raw "row violates row-level security policy" error.
        if (isAccessEndedError(error)) {
          setErr(tr("dailylog.err.accessEnded"));
          setSaving(false);
          refreshAccount?.();
          return;
        }
        // The feed covers this channel-day but the page hasn't seen its rows
        // (no counting orders yet, or loaded before the feed started): 046's
        // trigger refused it (NF001). Friendly text, then reload so the fed
        // rows appear. Not an access problem, so no refreshAccount.
        if (isFedDayError(error)) {
          setErr(tr("dailylog.feed.dayLocked"));
          setSaving(false);
          refreshWorkspaces?.();
          return;
        }
        // Unique-KOT index (migration 027) rejected a duplicate the pre-insert check
        // missed (e.g. an off-window date) → friendly message, not the raw PG error.
        if (error.code === "23505" || String(error.message||"").includes("daily_logs_kot_unique")) {
          setErr(tr("dailylog.err.kotDuplicate")); setSaving(false); return;
        }
        setErr(error.message); setSaving(false); return;
      }

      // Update local workspaces state — UI re-renders without page reload
      const newRow = {
        id: data.id,
        _dbId: data.id,
        createdAt: data.created_at || null,
        kotNumber: form.kot.trim(),
        date: logDate,
        dateObj: new Date(logDate + "T00:00:00"),
        gross,
        net,
        campaignFee: campFee,
        platform: ch?.platform,
        channelId: form.channelId,
        coFundRates,
        source: "manual",
      };
      if (setWorkspaces && wsId) {
        setWorkspaces(prev => {
          const next = {...prev};
          const w = {...next[wsId]};
          w.channels = {...w.channels};
          const channelEntry = {...w.channels[form.channelId]};
          channelEntry.history = [...(channelEntry.history||[]), newRow];
          w.channels[form.channelId] = channelEntry;
          next[wsId] = w;
          return next;
        });
      }

      setForm({channelId:channels[0]?.id||"",date:"",gross:"",net:"",campaignFee:"",kot:""});
      setAdding(false);
      if (isFirstRun && calc) {
        setFirstResult({ platform: ch?.platform, date: logDate, gross, net, netReceivable: calc.netReceivable || 0, commissionAmt: calc.commissionAmt || 0, cardFees: calc.cardFees || 0, totalFeeVat: calc.totalFeeVat || 0, coFund: cf.amount, effectiveNet: cf.effectiveNet });
        track("first_log_saved", { workspace_id: ws?.id, platform: ch?.platform });
      }

      // Safety net: refresh workspaces from DB after save to keep UI in sync
      if (refreshWorkspaces) {
        setTimeout(() => refreshWorkspaces(), 100);
      }
    } catch(e){
      setErr(tr("dailylog.err.saveFailed"));
    }
    setSaving(false);
  }

  const raw  = useMemo(()=>filterHistory(sourceHistory, range, customFrom, customTo),[sourceHistory, range, customFrom, customTo]);
  const rows = useMemo(()=>raw.map(r=>({...r,coFund:coFundAmount(r),...calcDaily(r.gross,effNet(r),r.cpc,{aggCommission:r.commission ?? cfg.aggCommission,cardFee:r.cardFeePct ?? cfg.cardFee,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true})}))
    // Newest DAY first; within the same day, the LATEST entry (most recent
    // created_at) sits at the TOP, so a newly added row appears first instead of
    // in arbitrary DB order. Falls back gracefully when created_at is missing.
    .sort((a,b)=>{
      const da = a.dateObj instanceof Date ? a.dateObj.getTime() : 0;
      const db = b.dateObj instanceof Date ? b.dateObj.getTime() : 0;
      if (db !== da) return db - da;
      const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return cb - ca;
    }),[raw,cfg,ws]);
  const t    = useMemo(()=>{
    // Sum the per-row calcDaily fields using the central function's field names so
    // applyPeriodAdjustments can roll them up. `netTicket` is kept separately for
    // the display (net-ticket total, distinct from net receivable).
    const base = rows.reduce((a,r)=>({
    gross:a.gross+r.gross,
    netTicket:a.netTicket+(r.net||0),
    coFund:a.coFund+(r.coFund||0),
    cpc:a.cpc+(r.cpc||0),
    netReceivable:a.netReceivable+r.netReceivable,
    cogs:a.cogs+r.cogs,
    cardFees:a.cardFees+r.cardFees,
    commission:a.commission+(r.commissionAmt||0),
    vatOnComm:a.vatOnComm+(r.vatOnComm||0),
    vatOnCpc:a.vatOnCpc+(r.vatOnCpc||0),
    vatOnCard:a.vatOnCard+(r.vatOnCard||0),
  }),{gross:0,netTicket:0,coFund:0,cpc:0,netReceivable:0,cogs:0,cardFees:0,commission:0,vatOnComm:0,vatOnCpc:0,vatOnCard:0});
    // Subscription fee is intentionally NOT applied in the Daily Log — it's a
    // fixed MONTHLY overhead, not a sales-driven cost, so it lives in Overview +
    // Insight only. (Owner decision: the Daily Log shows sales-driven costs —
    // commission, card, CPC, COGS — so a no-sales day doesn't show a fixed-fee loss.)
    // CPC for the period from the allocations (FULL committed spend, including
    // in-period days of a range with no sales entry) — deducted off net
    // receivable + profit so it matches Insights and the CPC table. Per-row
    // calcDaily already deducted CPC for days that DO have an entry (base.cpc).
    const cpcChannels = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels||{});
    const cpcAllocsT = cpcChannels.flatMap(c => (c.cpcAllocations||[]).map(a=>({...a, channelId:a.channelId??c.id})));
    // CPC window from the SHARED helper (cutoff/rangeEnd → winFrom/winTo, capped
    // at today) — one formula for row-selection and the CPC window alike. The
    // P&L counts CPC only for current+past days, never future days of a range.
    const { winFrom, winTo } = rangeWindow(range, customFrom, customTo);
    const vatR = ws?.vatRate ?? 0;
    // Bare CPC ad-spend topped up for in-period days with NO sales entry (before
    // VAT) — display-only, surfaced in the totals info tooltip so the period
    // total reconciles with the visible per-row CPC. Captured BEFORE the central
    // call mutates base.cpc to the full period amount.
    const cpcTopUp = +(Math.max(0, cpcForPeriod(cpcAllocsT, winFrom, winTo) - base.cpc)).toFixed(2);
    // ── Period-level P&L adjustments — SINGLE source of truth (calc.js) ───
    // Daily Log = CPC top-up + VAT, NO subscription. calcDaily untouched.
    applyPeriodAdjustments(base, {
      allocs: cpcAllocsT, winFrom, winTo, vatRate: vatR, includeSubscription: false,
    });
    // Map the central function's field names to the names this page renders.
    return {
      gross: base.gross,
      netTicket: base.netTicket,
      coFund: +(base.coFund||0).toFixed(2),
      net: base.netReceivable,
      cpc: base.cpc,
      profit: base.profit,
      vat: base.vat,
      cogs: base.cogs,
      card: base.cardFees,
      commission: base.commission,
      vatComm: base.vatOnComm,
      vatCpc: base.vatOnCpc,
      vatCard: base.vatOnCard,
      cpcTopUp,
    };
  },[rows,chId,ws,range,customFrom,customTo]);

  // ── CPC / campaign-spend allocations ─────────────────────────────────
  // CPC is now stored as date-range allocations (channel.cpcAllocations),
  // not per daily-log row. Collect the in-scope channels' allocations, tag
  // each with its channel's id/platform/colour for display, newest first.
  const cpcRows = useMemo(()=>{
    const scope = chId && ws?.channels?.[chId]
      ? [ws.channels[chId]]
      : Object.values(ws?.channels||{});
    // Date-range filter: show allocations that OVERLAP the selected period. The CPC
    // table shows full committed amounts, so the upper bound is NOT capped at today
    // (unlike the P&L net-receivable calc) — use the window's rangeEnd, not winTo.
    // ALL → no filter (winFrom null).
    const ymdL = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const { rangeEnd, winFrom } = rangeWindow(range, customFrom, customTo);
    const fFrom = winFrom;                       // null for ALL → lower bound open
    const fTo   = winFrom===null ? null : ymdL(rangeEnd);  // uncapped upper bound
    const overlaps = a => (fFrom===null || String(a.dateTo||"") >= fFrom) && (fTo===null || String(a.dateFrom||"") <= fTo);
    const out = [];
    scope.forEach(ch => {
      (ch.cpcAllocations||[]).filter(overlaps).forEach(a => out.push({
        ...a,
        channelId: a.channelId ?? ch.id,
        platform:  ch.platform,
        color:     ch.color || C.muted2,
      }));
    });
    return out.sort((a,b)=>String(b.dateFrom||"").localeCompare(String(a.dateFrom||"")));
  },[ws,chId,range,customFrom,customTo]);
  const cpcTotals = useMemo(()=>{
    const vatRate = ws?.vatRate ?? 0;
    return cpcRows.reduce((acc,a)=>{
      const amt = a.cpc||0;
      acc.cpc += amt;
      acc.vat += amt * vatRate;
      return acc;
    },{cpc:0,vat:0});
  },[cpcRows,ws]);

  // ── Platform contribution (co-funded discounts) ──────────────────────
  // ALL entries for the in-scope channel(s), newest dateFrom first. Deliberately
  // NOT filtered by the toolbar date window (unlike the CPC table): the Daily Log
  // opens on "Today" and entries are usually added AFTER a promo ends, so a
  // window filter would hide the entry just saved. The operator would then think
  // the save failed, and a re-entry would be rejected as an overlap with an entry
  // they cannot see. Entries are few and behave like configuration.
  // `amount` = the platform contribution earned on this entry's rows over its
  // WHOLE date range (only rows whose WINNING entry is this one; lib/coFund
  // sumCoFundByEntry). The subtitle says so; the Daily log tab's "Platform
  // contrib." column is the selected-period figure. With range "All" the two
  // totals are equal.
  const coFundRows = useMemo(()=>{
    const scope = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels||{});
    const out = [];
    scope.forEach(ch => {
      const byEntry = sumCoFundByEntry(ch.history || []);
      (ch.coFundAllocations||[]).forEach(a => out.push({
        ...a,
        channelId: a.channelId ?? ch.id,
        platform:  ch.platform,
        color:     ch.color || C.muted2,
        amount:    byEntry[a.id] || 0,
      }));
    });
    return out.sort((a,b)=>String(b.dateFrom||"").localeCompare(String(a.dateFrom||"")));
  },[ws,chId]);
  const coFundTotals = useMemo(()=>({ amount: +coFundRows.reduce((s,a)=>s+(a.amount||0),0).toFixed(2) }),[coFundRows]);
  // The add form offers the SAME channel scope as the list above: with a
  // platform pill selected, only that platform (pre-selected), so a saved entry
  // always lands in the visible list and an overlap error always points at a
  // row the operator can see; with "All", every platform.
  const coFundScopeCh = chId && ws?.channels?.[chId] ? ws.channels[chId] : null;
  const coFundFormChannels = coFundScopeCh ? [coFundScopeCh] : channels;
  const coFundFormChannelId = coFundScopeCh ? coFundScopeCh.id : coFundForm.channelId;
  // db.js could not read platform_cofunding this load (a real error, not a
  // missing table): every figure excludes co-funding and the entry list is NOT
  // known to be empty, so warn and never offer a form that could save a duplicate.
  const coFundLoadFailed = ws?.coFundLoadFailed === true;

  // Save (insert or update) a CPC allocation, then refresh so per-day CPC
  // re-derives across the daily-log table.
  async function saveCpc(){
    if(!cpcForm.channelId||!cpcForm.dateFrom||!cpcForm.dateTo||cpcForm.amount===""){
      setCpcErr(tr("dailylog.err.cpcAllRequired")); return;
    }
    if(cpcForm.dateTo < cpcForm.dateFrom){
      setCpcErr(tr("dailylog.err.endBeforeStart")); return;
    }
    setCpcSaving(true); setCpcErr("");
    const payload = {
      workspace_id: ws.id,
      channel_id:   cpcForm.channelId,
      platform:     ws.channels[cpcForm.channelId]?.platform,
      date_from:    cpcForm.dateFrom,
      date_to:      cpcForm.dateTo,
      cpc_amount:   parseFloat(cpcForm.amount)||0,
    };
    try {
      const { error } = await insertCpcAllocation(payload);
      if(error){ if(accessRouted(error)){ setCpcErr(tr("dailylog.err.accessEnded")); setCpcSaving(false); return; } setCpcErr(error.message); setCpcSaving(false); return; }
      setCpcForm({channelId:"",dateFrom:"",dateTo:"",amount:""});
      setCpcAdding(false);
      if(refreshWorkspaces) await refreshWorkspaces();
    } catch(e){
      setCpcErr(tr("dailylog.err.saveFailed"));
    }
    setCpcSaving(false);
  }
  // Inline edit of an existing CPC allocation row (mirrors the daily-log inline edit).
  function startCpcEdit(a){
    setCpcEditingId(a.id);
    setCpcEditForm({ dateFrom:a.dateFrom, dateTo:a.dateTo, amount:String(a.cpc ?? "") });
  }
  function cancelCpcEdit(){
    setCpcEditingId(null);
    setCpcEditForm({dateFrom:"",dateTo:"",amount:""});
  }
  async function saveCpcEdit(a){
    if(!cpcEditForm.dateFrom||!cpcEditForm.dateTo||cpcEditForm.amount===""){ alert(tr("dailylog.err.cpcDatesAmountRequired")); return; }
    if(cpcEditForm.dateTo < cpcEditForm.dateFrom){ alert(tr("dailylog.err.endBeforeStart")); return; }
    setCpcEditSaving(true);
    try {
      const { error } = await updateCpcAllocation(a.id, {
        date_from:  cpcEditForm.dateFrom,
        date_to:    cpcEditForm.dateTo,
        cpc_amount: parseFloat(cpcEditForm.amount)||0,
      });
      if(error){ if(accessRouted(error)){ setCpcEditSaving(false); return; } alert(tr("dailylog.alert.updateFailedPrefix") + error.message); setCpcEditSaving(false); return; }
      cancelCpcEdit();
      if(refreshWorkspaces) await refreshWorkspaces();
    } catch(e){
      alert(tr("dailylog.alert.updateFailedConn"));
    }
    setCpcEditSaving(false);
  }
  async function deleteCpc(id){
    if(!window.confirm(tr("dailylog.confirm.deleteCpc"))) return;
    const { error } = await deleteCpcAllocation(id);
    if(error){ if(accessRouted(error)) return; alert(tr("dailylog.alert.deleteFailedPrefix") + error.message); return; }
    if(refreshWorkspaces) await refreshWorkspaces();
  }
  function openCpcForm(){
    const defaultCh = chId || channels[0]?.id || "";
    setCpcForm({channelId:defaultCh,dateFrom:"",dateTo:"",amount:""});
    setCpcErr("");
    setCpcAdding(true);
  }

  // ── Platform contribution CRUD (mirrors saveCpc / saveCpcEdit / deleteCpc) ──
  // Validation is the shared pure helper (lib/coFund validateCoFundInput): channel +
  // both dates + discount % in (0,100] + co-fund % in [COFUND_PCT_MIN, COFUND_PCT_MAX]
  // + NO overlap with another entry on the same channel. Every write ERROR goes
  // through accessRouted. A 0-row update/delete with NO error (RLS USING excluded
  // the row: trial/subscription lapsed, operator lost location_access, or a
  // teammate already deleted it) is never silent: refreshAccount (paywall routing
  // if access ended) + refreshWorkspaces + close the editor + tell the user.
  // refreshWorkspaces() re-derives every row.
  // The % fields are passed to validateCoFundInput as the RAW typed TEXT, so it
  // can tell "blank" (allRequired) from "typed but not a number" (badNumber);
  // fractions are only built (pctInputToFraction) AFTER validation passes.
  function coFundErrText(code, input, existing, excludeId){
    if(code==="allRequired")    return tr("dailylog.cofund.err.allRequired");
    if(code==="badNumber"){
      const bad = v => typeof v === "string" && v.trim() !== "" && !Number.isFinite(pctInputToFraction(v));
      const bd = bad(input.discountPct), bc = bad(input.cofundPct);
      if(bd && !bc) return tr("dailylog.cofund.err.badNumberDiscount");
      if(bc && !bd) return tr("dailylog.cofund.err.badNumberCofund");
      return tr("dailylog.cofund.err.badNumber");
    }
    if(code==="endBeforeStart") return tr("dailylog.err.endBeforeStart");
    if(code==="discountRange")  return tr("dailylog.cofund.err.discountRange");
    if(code==="cofundRange")    return `${tr("dailylog.cofund.err.cofundRangePrefix")}${Math.round(COFUND_PCT_MIN*100)}${tr("dailylog.cofund.err.cofundRangeMid")}${Math.round(COFUND_PCT_MAX*100)}${tr("dailylog.cofund.err.cofundRangeSuffix")}`;
    if(code==="overlap"){
      const o = findCoFundOverlap(existing, input, excludeId);
      const span = o ? `${fmtDateCell(new Date(o.dateFrom+"T00:00:00"))} – ${fmtDateCell(new Date(o.dateTo+"T00:00:00"))}` : "";
      // Name the platform of the clashing entry (the list is scoped like the form, so it is on screen).
      const platform = ws?.channels?.[o?.channelId ?? input.channelId]?.platform || "";
      return `${tr("dailylog.cofund.err.overlapPrefix")}${platform}${tr("dailylog.cofund.err.overlapMid")}${span}${tr("dailylog.cofund.err.overlapSuffix")}`;
    }
    return tr("dailylog.err.saveFailed");
  }
  async function saveCoFund(){
    if(coFundLoadFailed){ setCoFundErr(tr("dailylog.cofund.loadFailed")); return; }
    const input = {
      channelId: coFundFormChannelId, dateFrom: coFundForm.dateFrom, dateTo: coFundForm.dateTo,
      discountPct: coFundForm.discountPct, cofundPct: coFundForm.cofundPct,
    };
    const existing = ws?.channels?.[input.channelId]?.coFundAllocations || [];
    const code = validateCoFundInput(input, existing, null);
    if(code){ setCoFundErr(coFundErrText(code, input, existing, null)); return; }
    setCoFundSaving(true); setCoFundErr("");
    const payload = {
      workspace_id: ws.id,
      channel_id:   input.channelId,
      platform:     ws.channels[input.channelId]?.platform,
      date_from:    input.dateFrom,
      date_to:      input.dateTo,
      discount_pct: pctInputToFraction(input.discountPct),
      cofund_pct:   pctInputToFraction(input.cofundPct),
    };
    try {
      const { error } = await insertCoFundAllocation(payload);
      if(error){
        if(accessRouted(error)){ setCoFundErr(tr("dailylog.err.accessEnded")); setCoFundSaving(false); return; }
        // 046's guard (NF001): a Platform contribution may not cover a fed
        // channel's days. Catalog text, never the database's English message.
        setCoFundErr(isFedDayError(error) ? tr("dailylog.cofund.err.fedDays") : error.code==="23514" ? tr("dailylog.cofund.err.invalidValues") : error.message); setCoFundSaving(false); return;
      }
      setCoFundForm(EMPTY_COFUND_FORM);
      setCoFundAdding(false);
      if(refreshWorkspaces) await refreshWorkspaces();
    } catch(e){
      setCoFundErr(tr("dailylog.err.saveFailed"));
    }
    setCoFundSaving(false);
  }
  function startCoFundEdit(a){
    setCoFundEditingId(a.id);
    setCoFundEditForm({ dateFrom:a.dateFrom, dateTo:a.dateTo, discountPct:fractionToPctInput(a.discountPct), cofundPct:fractionToPctInput(a.cofundPct) });
  }
  function cancelCoFundEdit(){
    setCoFundEditingId(null);
    setCoFundEditForm({dateFrom:"",dateTo:"",discountPct:"",cofundPct:""});
  }
  async function saveCoFundEdit(a){
    if(coFundLoadFailed){ alert(tr("dailylog.cofund.loadFailed")); return; }
    const input = {
      channelId: a.channelId, dateFrom: coFundEditForm.dateFrom, dateTo: coFundEditForm.dateTo,
      discountPct: coFundEditForm.discountPct, cofundPct: coFundEditForm.cofundPct,
    };
    const existing = ws?.channels?.[a.channelId]?.coFundAllocations || [];
    // excludeId = a.id: the entry being edited never overlaps itself.
    const code = validateCoFundInput(input, existing, a.id);
    if(code){ alert(coFundErrText(code, input, existing, a.id)); return; }
    setCoFundEditSaving(true);
    try {
      const { data, error } = await updateCoFundAllocation(a.id, {
        date_from: input.dateFrom, date_to: input.dateTo,
        discount_pct: pctInputToFraction(input.discountPct), cofund_pct: pctInputToFraction(input.cofundPct),
      });
      if(error){
        if(accessRouted(error)){ setCoFundEditSaving(false); return; }
        // NF001: an entry that overlaps a fed window can be shortened or deleted, never kept over it.
        alert(isFedDayError(error) ? tr("dailylog.cofund.err.fedDays") : error.code==="23514" ? tr("dailylog.cofund.err.invalidValues") : tr("dailylog.alert.updateFailedPrefix") + error.message);
        setCoFundEditSaving(false); return;
      }
      if(Array.isArray(data) && data.length === 0){
        // 0 rows, no error: RLS USING hid the row, or it no longer exists.
        refreshAccount?.();   // routes to the paywall if the trial/subscription ended
        cancelCoFundEdit();
        if(refreshWorkspaces) await refreshWorkspaces();
        setCoFundEditSaving(false);
        alert(tr("dailylog.cofund.err.goneOrNoAccess"));
        return;
      }
      cancelCoFundEdit();
      if(refreshWorkspaces) await refreshWorkspaces();
    } catch(e){
      alert(tr("dailylog.alert.updateFailedConn"));
    }
    setCoFundEditSaving(false);
  }
  async function deleteCoFund(id){
    if(!window.confirm(tr("dailylog.cofund.confirmDelete"))) return;
    const { data, error } = await deleteCoFundAllocation(id);
    if(error){ if(accessRouted(error)) return; alert(tr("dailylog.alert.deleteFailedPrefix") + error.message); return; }
    if(Array.isArray(data) && data.length === 0){
      // 0 rows, no error: RLS USING hid the row, or it no longer exists.
      refreshAccount?.();
      if(coFundEditingId === id) cancelCoFundEdit();
      if(refreshWorkspaces) await refreshWorkspaces();
      alert(tr("dailylog.cofund.err.goneOrNoAccess"));
      return;
    }
    if(refreshWorkspaces) await refreshWorkspaces();
  }
  function openCoFundForm(){
    setCoFundForm({...EMPTY_COFUND_FORM, channelId: coFundScopeCh?.id || channels[0]?.id || ""});
    setCoFundErr("");
    setCoFundAdding(true);
  }

  // Custom date range for export
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo,   setExportTo]   = useState("");
  const [showCustomExport, setShowCustomExport] = useState(false);

  // Delete a log entry
  async function deleteEntry(r){
    // Fed rows have no Delete button (the feed owns them); never send one.
    if(isFedRow(r)) return;
    const rowId = r?.id, dbId = r?._dbId;
    if(!window.confirm(tr("dailylog.confirm.deleteLog"))) return;
    const targetId = dbId || rowId;
    if(!targetId){ alert(tr("dailylog.alert.cannotIdentifyDelete")); return; }
    const { error } = await deleteDailyLog(targetId);
    if(error){
      if(accessRouted(error)) return;
      if(isFedDayError(error)){ alert(tr("dailylog.feed.dayLocked")); refreshWorkspaces?.(); return; }
      alert(tr("dailylog.alert.deleteFailedPrefix") + error.message);
      return;
    }
    // Update local state — UI re-renders without page reload
    if (setWorkspaces && wsId) {
      setWorkspaces(prev => {
        const next = {...prev};
        const w = {...next[wsId]};
        w.channels = {...w.channels};
        Object.keys(w.channels).forEach(cid => {
          const c = {...w.channels[cid]};
          c.history = (c.history||[]).filter(h => h.id !== rowId && h._dbId !== targetId && h.id !== targetId);
          w.channels[cid] = c;
        });
        if(w.history) w.history = w.history.filter(h => h.id !== rowId && h._dbId !== targetId && h.id !== targetId);
        next[wsId] = w;
        return next;
      });
    }
  }

  // ── Inline row editing ───────────────────────────────────────────────
  function startEdit(r){
    // A fed row's date, KOT, gross and net come from the feed (046 refuses
    // changes to them) and the web has no other editable cell: no editor.
    if(isFedRow(r)) return;
    setEditingId(r.id);
    setEditForm({
      date:        r.date || (r.dateObj instanceof Date && !isNaN(r.dateObj.getTime()) ? ymd(r.dateObj) : ""),
      gross:       r.gross ?? "",
      net:         r.net ?? "",
      campaignFee: r.campaignFee ?? "",
      kot:         r.kotNumber ?? "",
    });
  }
  function cancelEdit(){
    setEditingId(null);
    setEditForm({date:"",gross:"",net:"",campaignFee:"",kot:""});
  }
  async function saveEdit(r){
    // Never reached for a fed row (startEdit opens no editor), and never sends
    // its sales, date or KOT if it were.
    if(isFedRow(r)){ cancelEdit(); return; }
    const targetId = r._dbId || r.id;
    if(!targetId){ alert(tr("dailylog.alert.cannotIdentifyEdit")); return; }
    const gross = parseFloat(editForm.gross)||0;
    const net   = parseFloat(editForm.net)||0;
    const campFee = parseFloat(editForm.campaignFee)||0;
    const logDate = editForm.date;
    if(!logDate){ alert(tr("dailylog.alert.dateRequired")); return; }
    if(net > gross){ alert(tr("dailylog.alert.netAboveGross")); return; }
    // A typed row can't move onto a day the feed covers (046 refuses it too).
    // Edited in place, on its own day, it saves as before.
    {
      const chHist = (r.channelId ? ws?.channels?.[r.channelId]?.history : null) || [];
      if(logDate !== rowDay(r) && hasFedRowOnDay(chHist, logDate)){
        alert(tr("dailylog.feed.dayLocked")); return;
      }
    }
    // KOT stays unique per platform + date — block a collision with another row.
    {
      const kotVal = (editForm.kot||"").trim().toLowerCase();
      const chHist = (r.channelId ? ws?.channels?.[r.channelId]?.history : null) || [];
      if(kotVal && chHist.some(x => (x._dbId||x.id) !== targetId && String(x.date) === String(logDate) && String(x.kotNumber ?? "").trim().toLowerCase() === kotVal)){
        alert(tr("dailylog.err.kotDuplicate")); return;
      }
    }
    setEditSaving(true);
    try {
      const ch = r.channelId ? ws?.channels?.[r.channelId] : null;
      // Co-fund rates for the (possibly changed) log date — also re-stamped on
      // the optimistic row below so it never shows a stale entry's rates.
      const coFundRates = ch ? coFundRatesFor(ch.coFundAllocations || [], logDate, ch.id) : (r.coFundRates ?? null);
      // Recompute the stored P&L fields using the EXISTING calcDaily — no
      // formula change, just recalculated for the edited inputs (effective net).
      const calc = calcDaily(gross, coFundBreakdown(gross, net, coFundRates).effectiveNet, 0, {
        aggCommission: r.commission ?? ch?.commission ?? cfg.aggCommission,
        cardFee:       r.cardFeePct ?? ch?.cardFeePct ?? cfg.cardFee,
        vatRate:       ws?.vatRate ?? cfg.vatRate,
        cogsPct:       ws?.cogsPct ?? cfg.cogsPct,
        vatOnFees:     ws?.vatOnFees === true,
      });
      const { error } = await updateDailyLog(targetId, {
        log_date:          logDate,
        kot_number:        editForm.kot?.trim() || null,
        gross,
        net_sales:         net,
        campaign_fee:      campFee,
        net_receivable:    calc?.netReceivable ?? null,
        vat:               calc?.vat ?? null,
        earning_after_vat: calc?.earningAfterVat ?? null,
        cogs:              calc?.cogs ?? null,
        true_profit:       calc?.trueProfit ?? null,
      });
      if(error){
        if(accessRouted(error)){ setEditSaving(false); return; }
        if(isFedDayError(error)){ alert(tr("dailylog.feed.dayLocked")); setEditSaving(false); refreshWorkspaces?.(); return; }
        if (error.code === "23505" || String(error.message||"").includes("daily_logs_kot_unique")) { alert(tr("dailylog.err.kotDuplicate")); setEditSaving(false); return; }
        alert(tr("dailylog.alert.updateFailedPrefix") + error.message); setEditSaving(false); return;
      }

      // Update local state so the row reflects immediately.
      if (setWorkspaces && wsId) {
        setWorkspaces(prev => {
          const next = {...prev};
          const w = {...next[wsId]};
          w.channels = {...w.channels};
          Object.keys(w.channels).forEach(cid => {
            const c = {...w.channels[cid]};
            c.history = (c.history||[]).map(h =>
              (h.id===r.id || h._dbId===targetId)
                ? {...h, date: logDate, dateObj: new Date(logDate+"T00:00:00"), gross, net, campaignFee:campFee, kotNumber:(editForm.kot||"").trim(), coFundRates}
                : h
            );
            w.channels[cid] = c;
          });
          if(w.history) w.history = w.history.map(h =>
            (h.id===r.id || h._dbId===targetId)
              ? {...h, date: logDate, dateObj: new Date(logDate+"T00:00:00"), gross, net, campaignFee:campFee, kotNumber:(editForm.kot||"").trim(), coFundRates}
              : h
          );
          next[wsId] = w;
          return next;
        });
      }
      cancelEdit();
      if (refreshWorkspaces) setTimeout(()=>refreshWorkspaces(), 100);
    } catch(e){
      alert(tr("dailylog.alert.updateFailedConn"));
    }
    setEditSaving(false);
  }

  function exportCustomRange(){
    if(!exportFrom || !exportTo){
      alert(tr("dailylog.alert.pickBothDates"));
      return;
    }
    const start = new Date(exportFrom + "T00:00:00");
    const end   = new Date(exportTo   + "T23:59:59");
    if(start > end){ alert(tr("dailylog.alert.startBeforeEnd")); return; }
    const filtered = sourceHistory.filter(r => r.dateObj >= start && r.dateObj <= end);
    exportRowsToCSV(filtered, `${exportFrom}_to_${exportTo}`);
  }

  function exportRowsToCSV(sourceRows, rangeLabel){
    const exportRows = sourceRows.map(r=>({...r,coFund:coFundAmount(r),...calcDaily(r.gross,effNet(r),r.cpc,{aggCommission:r.commission ?? cfg.aggCommission,cardFee:r.cardFeePct ?? cfg.cardFee,vatRate:ws?.vatRate??cfg.vatRate,cogsPct:ws?.cogsPct??cfg.cogsPct,vatOnFees:ws?.vatOnFees===true})}));
    if(exportRows.length===0){
      alert(tr("dailylog.alert.noDataToExport"));
      return;
    }
    const fmtD = (d) => {
      if(!(d instanceof Date)) return "";
      const dd = String(d.getDate()).padStart(2,"0");
      const mm = String(d.getMonth()+1).padStart(2,"0");
      return `${dd}/${mm}/${d.getFullYear()}`;
    };
    const headers=["Channel","Date","Gross","Net Ticket","Discount","Platform Contrib.","Commission","VAT on Commission","CPC","VAT on CPC","Card Fees","VAT on Card Fees","Net Receivable","VAT","COGS","True Profit","Net Profit %"];
    const csvRows=exportRows.map(r=>{
      const platform = r.platform || (chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "");
      const discount = Math.max(0, (r.gross||0) - (r.net||0));
      return [
        platform,
        fmtD(r.dateObj),
        (r.gross||0).toFixed(2),
        (r.net||0).toFixed(2),
        discount.toFixed(2),
        (r.coFund||0).toFixed(2),
        (r.commissionAmt||0).toFixed(2),
        (r.vatOnComm||0).toFixed(2),
        (r.cpc||0).toFixed(2),
        (r.vatOnCpc||0).toFixed(2),
        (r.cardFees||0).toFixed(2),
        (r.vatOnCard||0).toFixed(2),
        (r.netReceivable||0).toFixed(2),
        (r.vat||0).toFixed(2),
        (r.cogs||0).toFixed(2),
        (r.trueProfit||0).toFixed(2),
        ((r.netProfitPct||0)*100).toFixed(2)+"%"
      ].join(",");
    });
    const csv=[headers.join(","),...csvRows].join("\n");
    const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    const platformLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "all";
    a.href=url; a.download=`netrev-${(ws?.name||"export").replace(/\s+/g,"-")}-${platformLabel}-${rangeLabel}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function exportCSV(exportRange){
    const filtered = filterHistory(sourceHistory, exportRange);
    exportRowsToCSV(filtered, exportRange);
  }

  // Styled .xlsx export — same template the daily email report uses, scoped to
  // the dates/channel currently shown. Pulls from the backend (which recomputes
  // via the shared calc), so it matches on-screen + the email exactly. Falls
  // back to the client-side CSV if the backend call fails.
  const ymd = d => {
    if(!(d instanceof Date) || isNaN(d.getTime())) return "";
    const mm=String(d.getMonth()+1).padStart(2,"0"), dd=String(d.getDate()).padStart(2,"0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };
  async function exportXlsx(){
    if(!rows || rows.length===0){
      alert(tr("dailylog.alert.noDataToExport"));
      return;
    }
    // Window MUST match the on-screen totals/range so the xlsx CPC ties out with
    // the page (the backend tops up CPC over [from,to] from the allocations). For
    // "All", widen to cover every in-scope CPC allocation range too — otherwise a
    // range extending beyond the last sales entry would be clipped and CPC
    // under-counted vs the on-screen "All" total and the CPC table.
    let from, to;
    if(range==="CUSTOM" && customFrom && customTo){ from = customFrom; to = customTo; }
    else if(range==="ALL"||range==="all"||range==="All"){
      const times = rows.map(r=>r.dateObj).filter(d=>d instanceof Date && !isNaN(d.getTime())).map(d=>d.getTime());
      from = times.length ? ymd(new Date(Math.min(...times))) : "";
      to   = times.length ? ymd(new Date(Math.max(...times))) : "";
      const scopeChs = chId && ws?.channels?.[chId] ? [ws.channels[chId]] : Object.values(ws?.channels||{});
      scopeChs.forEach(c => (c.cpcAllocations||[]).forEach(a => {
        if(a.dateFrom && (!from || a.dateFrom < from)) from = a.dateFrom;
        if(a.dateTo   && (!to   || a.dateTo   > to))   to   = a.dateTo;
      }));
    } else {
      // Today / Yesterday / rolling presets — derive from the SHARED window
      // helper (winFrom = cutoff, winTo capped at today).
      const w = rangeWindow(range, customFrom, customTo);
      from = w.winFrom ?? ""; to = w.winTo ?? "";
    }
    // The report P&L counts CPC only for current+past days — never future days.
    const todayY = ymd(new Date());
    if(to && to > todayY) to = todayY;
    try {
      const session = await getCurrentSession();
      if(!session?.access_token) throw new Error("Not signed in");
      const params = new URLSearchParams();
      if(from) params.set("from", from);
      if(to)   params.set("to", to);
      if(chId) params.set("channelId", chId);
      const resp = await fetch(`${API_BASE}/api/workspaces/${wsId}/logs.xlsx?${params.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if(!resp.ok) throw new Error(`export failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const platformLabel = chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "all";
      a.href=url; a.download=`netrev-${(ws?.name||"export").replace(/\s+/g,"-")}-${platformLabel.replace(/\s+/g,"-")}-${from||"all"}_to_${to||"all"}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) {
      console.warn("xlsx export failed, falling back to CSV:", e);
      exportRowsToCSV(rows, range==="ALL"?"all":range);
    }
  }

  // The Export button now lives in the top toolbar (App.js); it bumps
  // exportTick to ask this component (which has the filtered rows) to export.
  const exportTickRef = useRef(exportTick);
  useEffect(()=>{
    if(exportTick !== exportTickRef.current){
      exportTickRef.current = exportTick;
      exportXlsx();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[exportTick]);

  // The toolbar's "+ Add CPC spend" button bumps cpcAddTick (after switching to
  // the CPC tab) to ask this component to open the CPC allocation add form.
  const cpcAddTickRef = useRef(cpcAddTick);
  useEffect(()=>{
    if(cpcAddTick !== cpcAddTickRef.current){
      cpcAddTickRef.current = cpcAddTick;
      openCpcForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[cpcAddTick]);

  // The toolbar's "+ Add platform contribution" button bumps coFundAddTick (after
  // switching to the cofund tab) to open the add form. Ref SEEDED from the prop so
  // a remount (key={wsId}) never auto-opens it from a stale tick.
  const coFundAddTickRef = useRef(coFundAddTick);
  useEffect(()=>{
    if(coFundAddTick !== coFundAddTickRef.current){
      coFundAddTickRef.current = coFundAddTick;
      openCoFundForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[coFundAddTick]);

  // ── CSV import / export (deterministic, no AI) ───────────────────────
  const channelByPlatform = (name) => {
    const n = String(name||"").trim().toLowerCase();
    return channels.find(c => String(c.platform||"").trim().toLowerCase() === n);
  };
  // Accept DD/MM/YYYY or YYYY-MM-DD; return YYYY-MM-DD (Supabase format) or null.
  const toYMD = (s) => {
    s = String(s||"").trim();
    if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if(m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
    return null;
  };
  const ddmmyyyy = (d) => {
    if(!(d instanceof Date) || isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
  };
  // ROUND-TRIP: exports the RAW customer net (r.net), never effNet(r). Platform co-funding entries are NOT part of this CSV and importDaily ignores them, so a re-import can never fold the platform contribution into net_sales.
  function buildDailyExportRows(){
    return sourceHistory.map(r => [
      r._dbId || r.id || "",
      r.platform || (r.channelId && ws?.channels?.[r.channelId] ? ws.channels[r.channelId].platform : ""),
      ddmmyyyy(r.dateObj),
      r.kotNumber ?? "",
      r.gross ?? 0,
      r.net ?? r.gross ?? 0,
      r.campaignFee ?? 0,
    ]);
  }
  async function importDaily(rows){
    let added=0, updated=0; const errors=[];
    // Every loaded row by id, with its channel (db.js rows carry no
    // channelId), so a CSV row can be matched to the row it would update.
    // And, built once, every channel-day that shows a fed row: with one row
    // per order a month can hold thousands of them, so the loop below never
    // scans a channel's history per CSV row.
    const loadedById = new Map();
    const fedDays = new Set();
    Object.values(ws?.channels||{}).forEach(c => (c.history||[]).forEach(h => {
      loadedById.set(String(h._dbId || h.id), { row: h, channelId: c.id });
      if(isFedRow(h)) fedDays.add(`${c.id}|${rowDay(h)}`);
    }));
    for(let i=0;i<rows.length;i++){
      const row=rows[i];
      const rowNo=i+2;
      const id = pick(row,"ID","Id");
      const loaded = id ? loadedById.get(String(id)) : null;
      // A fed row's ID (an exported file round-trips them): only its campaign
      // fee is the operator's. Its date, platform, KOT, gross and net come
      // from the feed and 046 refuses changes to them, so they're never sent,
      // whatever the file says. A fee equal to the loaded one sends nothing
      // and isn't counted: a re-imported export changes only what was edited.
      if(loaded && isFedRow(loaded.row)){
        const fee = parseFloat(pick(row,"Campaign Fee","Fee"))||0;
        if(fee === (+loaded.row.campaignFee||0)) continue;
        const { error } = await updateDailyLog(loaded.row._dbId || loaded.row.id, { campaign_fee: fee });
        if(error){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}: ${isFedDayError(error) ? tr("dailylog.feed.dayLocked") : error.message}`); continue; }
        updated++; continue;
      }
      const platform = pick(row,"Platform","Channel");
      const ch = channelByPlatform(platform);
      if(!ch){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}${tr("dailylog.import.unknownPlatform")}${platform||tr("dailylog.import.unknownPlatformBlank")}${tr("dailylog.import.unknownPlatformSuffix")}`); continue; }
      const log_date = toYMD(pick(row,"Date"));
      if(!log_date){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}${tr("dailylog.import.badDate")}`); continue; }
      const grossStr = pick(row,"Gross","Gross Sales");
      if(grossStr==="" || isNaN(parseFloat(grossStr))){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}${tr("dailylog.import.missingGross")}`); continue; }
      const gross = parseFloat(grossStr)||0;
      const netStr = pick(row,"Net","Net Sales","Net Ticket");
      const net = netStr!=="" && !isNaN(parseFloat(netStr)) ? parseFloat(netStr) : gross;
      if(net > gross){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}${tr("dailylog.import.netExceedsGrossA")}${net}${tr("dailylog.import.netExceedsGrossB")}${gross}${tr("dailylog.import.netExceedsGrossC")}`); continue; }
      const campFee = parseFloat(pick(row,"Campaign Fee","Fee"))||0;
      const calc = calcDaily(gross, net, 0, {
        aggCommission: ch.commission || 0,
        cardFee:       ch.cardFeePct || 0,
        vatRate:       ws?.vatRate ?? cfg.vatRate,
        cogsPct:       ws?.cogsPct ?? cfg.cogsPct,
      });
      const payload = {
        workspace_id: ws.id,
        channel_id: ch.id,
        platform: ch.platform,
        log_date,
        kot_number: pick(row,"KOT","KOT Number") || null,
        gross,
        net_sales: net,
        campaign_fee: campFee,
        discount_type: "none",
        net_receivable: calc?.netReceivable||null,
        vat: calc?.vat||null,
        earning_after_vat: calc?.earningAfterVat||null,
        cogs: calc?.cogs||null,
        true_profit: calc?.trueProfit||null,
      };
      if(id) payload.id = id;
      // Typed rows can never join a fed day: a new row, or a typed row moved
      // onto one, is skipped with the add form's text (046 refuses it too).
      // A typed row re-imported on its own day goes through as before.
      const movesOntoDay = !loaded || loaded.channelId !== ch.id || rowDay(loaded.row) !== log_date;
      if(movesOntoDay && fedDays.has(`${ch.id}|${log_date}`)){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}: ${tr("dailylog.feed.dayLocked")}`); continue; }
      const { error } = await upsertDailyLog(payload);
      if(error){ errors.push(`${tr("dailylog.import.rowPrefix")}${rowNo}: ${isFedDayError(error) ? tr("dailylog.feed.dayLocked") : error.message}`); continue; }
      if(id) updated++; else added++;
    }
    if(refreshWorkspaces) await refreshWorkspaces();
    return { added, updated, skipped: errors.length, errors };
  }

  const f = n => `${sym}${(n||0).toLocaleString("en-GB",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtDateCell = d => {
    if(!(d instanceof Date)) return "—";
    const dd=String(d.getDate()).padStart(2,"0");
    const mm=String(d.getMonth()+1).padStart(2,"0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  // Resolve a row's platform/channel name for display (#18).
  const rowChannelName = (r) => r.platform
    || (r.channelId && ws?.channels?.[r.channelId] ? ws.channels[r.channelId].platform : "")
    || (chId && ws?.channels?.[chId] ? ws.channels[chId].platform : "—");

  // Resolve a row's per-channel BRAND COLOUR — the same `channel.color` used by
  // the channel-selector pills, the Insights "Per-platform performance" dots and
  // the Insights PDF. Mirrors rowChannelName's fallback chain. enrichRow does
  // not stamp `color`, so resolve it from the workspace's channel objects.
  const rowChannelColor = (r) =>
    (r.channelId && ws?.channels?.[r.channelId] ? ws.channels[r.channelId].color : null)
    || (chId && ws?.channels?.[chId] ? ws.channels[chId].color : null)
    || C.muted2;

  // Muted, professional palette — monochrome by default. Emphasis comes from
  // weight and a single accent, not a rainbow of reds/blues per cell. Only
  // True Profit turns red, and only when negative (a genuine signal).
  const cNum   = C.text;        // primary figures
  const cMuted = C.muted;       // costs / deductions (subtle gray, not alarming)
  // "From UrbanPiper" pill on fed rows, in the Channel column so it stays in
  // view while the table scrolls sideways. The page's own blue tokens.
  const fedBadgeStyle = {display:"inline-block",fontSize:10.5,fontWeight:700,letterSpacing:".2px",padding:"1px 7px",borderRadius:999,background:C.blue10,color:C.blueDark,border:`1px solid ${C.blue20}`,whiteSpace:"nowrap",lineHeight:1.5};
  // Each column carries sort metadata (sk=key, st=type, sv=value accessor) so
  // the reusable sortable/filterable header can drive it. The action column has
  // no accessor → not sortable/filterable.
  // NOTE: `h` is the STABLE logic key (used by totalFor/sortColByHeader lookups
  // and c.h==="Channel"/"Date" comparisons) — it must stay in English. `label`
  // is the translated text actually rendered in the header cell.
  const COLS=[
    {h:"Channel",    label:tr("dailylog.col.channel"), sk:"channel", st:"text", sv:r=>rowChannelName(r), editKey:null, fn:r=>(
      <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
        <span style={{width:9,height:9,borderRadius:5,background:rowChannelColor(r),flexShrink:0}}/>
        <span style={{fontWeight:600,color:C.text}}>{rowChannelName(r)}</span>
        {isFedRow(r) && <span style={fedBadgeStyle}>{tr("dailylog.feed.badge")}</span>}
      </span>
    )},
    {h:"Date",       label:tr("dailylog.col.date"), sk:"date", st:"date", sv:r=>r.dateObj, editKey:"date", editType:"date", fn:r=><span style={{color:C.text,fontWeight:500}}>{fmtDateCell(r.dateObj)}</span>},
    {h:"KOT",        label:tr("dailylog.col.kot"), sk:"kot", st:"text", sv:r=>r.kotNumber||"", editKey:"kot", editType:"text", fn:r=><span style={{color:cMuted}}>{r.kotNumber||"—"}</span>},
    {h:`Gross (${sym.trim()})`, label:`${tr("dailylog.col.gross")} (${sym.trim()})`, sk:"gross", st:"number", sv:r=>r.gross, editKey:"gross", fn:r=><span style={{color:cNum,fontWeight:500}}>{f(r.gross)}</span>},
    {h:"Net Ticket", label:tr("dailylog.col.netTicket"), sk:"net", st:"number", sv:r=>r.net, editKey:"net", fn:r=><span style={{color:cNum}}>{f(r.net)}</span>},
    {h:"Discount",   label:tr("dailylog.col.discount"), sk:"discount", st:"number", sv:r=>Math.max(0,(r.gross||0)-(r.net||0)), editKey:null, fn:r=><span style={{color:cMuted}}>{f(Math.max(0,(r.gross||0)-(r.net||0)))}</span>},
    {h:"Platform contrib.", label:tr("dailylog.cofund.logCol"), sk:"coFund", st:"number", sv:r=>r.coFund||0, editKey:null, fn:r=><span style={{color:(r.coFund||0)>0?C.green:cMuted}}>{f(r.coFund)}</span>},
    {h:"Commission", label:tr("dailylog.col.commission"), sk:"commission", st:"number", sv:r=>r.commissionAmt, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.commissionAmt)}</span>},
    {h:"VAT/Comm",   label:tr("dailylog.col.vatComm"), sk:"vatComm", st:"number", sv:r=>r.vatOnComm, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.vatOnComm)}</span>},
    {h:"Card Fees",  label:tr("dailylog.col.cardFees"), sk:"card", st:"number", sv:r=>r.cardFees, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.cardFees)}</span>},
    {h:"VAT/Card",   label:tr("dailylog.col.vatCard"), sk:"vatCard", st:"number", sv:r=>r.vatOnCard, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.vatOnCard)}</span>},
    {h:"Net Recv.",  label:tr("dailylog.col.netRecv"), sk:"netRecv", st:"number", sv:r=>r.netReceivable, editKey:null, fn:r=><span style={{color:cNum,fontWeight:600}}>{f(r.netReceivable)}</span>},
    {h:"VAT",        label:tr("dailylog.col.vat"), sk:"vat", st:"number", sv:r=>r.vat, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.vat)}</span>},
    {h:"COGS",       label:tr("dailylog.col.cogs"), sk:"cogs", st:"number", sv:r=>r.cogs, editKey:null, fn:r=><span style={{color:cMuted}}>{f(r.cogs)}</span>},
    {h:"True Profit",label:tr("dailylog.col.trueProfit"), sk:"trueProfit", st:"number", sv:r=>r.trueProfit, editKey:null, fn:r=><span style={{fontWeight:700,color:(r.trueProfit<0?C.red:C.text)}}>{f(r.trueProfit)}</span>},
    {h:"",           label:"", editKey:null, isAction:true, fn:r=>{
      // Fed row: every editable cell (date, KOT, gross, net) is one 046
      // protects, so no Edit or Delete, just a lock that says why. A span,
      // not a button, so nothing treats it as an action.
      if(isFedRow(r)){
        const lockText = tr("dailylog.feed.rowLocked");
        return (
          <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
            <span role="img" aria-label={lockText} title={lockText}
              style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",color:C.muted2}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            </span>
          </div>
        );
      }
      const isEditing = editingId === r.id;
      if(isEditing){
        return (
          <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
            <button onClick={()=>saveEdit(r)} disabled={editSaving}
              style={{padding:"5px 10px",borderRadius:6,border:"none",background:C.blue,color:"#fff",cursor:editSaving?"wait":"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>
              {editSaving?tr("dailylog.btn.savingEllipsis"):tr("dailylog.btn.save")}
            </button>
            <button onClick={cancelEdit} disabled={editSaving}
              style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:500}}>
              {tr("dailylog.btn.cancel")}
            </button>
          </div>
        );
      }
      return (
        <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
          <button onClick={()=>startEdit(r)} title={tr("dailylog.btn.editEntry")}
            style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.bg3;e.currentTarget.style.color=C.text;e.currentTarget.style.borderColor=C.muted2;}}
            onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          </button>
          <button onClick={()=>deleteEntry(r)} title={tr("dailylog.btn.deleteEntry")}
            style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.red10;e.currentTarget.style.color=C.red;e.currentTarget.style.borderColor="#f8c9c9";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
          </button>
        </div>
      );
    }},
  ];

  // ── Sortable + filterable: daily-log table ───────────────────────────
  // Derive the reusable helper's column descriptors from COLS (the action
  // column has no `sk` → not sortable/filterable). The hook returns the
  // view-sorted/filtered rows; the period totals row below stays on the full
  // `t` (it reflects the selected period, not a transient column filter).
  const logSortCols = useMemo(()=>COLS.filter(c=>c.sk).map(c=>({
    key:c.sk, type:c.st, value:c.sv,
    align:(c.h==="Channel"?"left":(c.h==="Date"?"left":"right")),
  })),[COLS]);
  const logTable = useTableSortFilter(rows, logSortCols);
  const sortColByHeader = useMemo(()=>{
    const m={}; COLS.forEach(c=>{ if(c.sk) m[c.h]=logSortCols.find(sc=>sc.key===c.sk); }); return m;
  },[COLS,logSortCols]);

  // Compact inline-edit input used inside editable cells. `type` is "number"
  // (gross/net) or "date" (the log date) — date cells open the native picker.
  const editCellInput = (key, type="number") => (
    <input
      type={type}
      value={editForm[key]}
      onChange={e=>setEditForm(f=>({...f,[key]:e.target.value}))}
      {...(type==="date" ? { onClick:openDatePicker, onFocus:openDatePicker, max:new Date().toISOString().split("T")[0] } : {})}
      style={{width:type==="date"?140:(type==="text"?120:90),padding:"6px 8px",borderRadius:6,border:`1px solid ${C.blue}`,fontSize:13,fontFamily:"inherit",textAlign:(type==="date"||type==="text")?"left":"right",boxSizing:"border-box",fontVariantNumeric:"tabular-nums"}}
      autoFocus={key==="gross"}
    />
  );
  // Inline-edit inputs for an existing CPC allocation row.
  const cpcDateInput = (key) => (
    <input type="date" value={cpcEditForm[key]} onChange={e=>setCpcEditForm(f=>({...f,[key]:e.target.value}))}
      onClick={openDatePicker} onFocus={openDatePicker}
      {...(key==="dateTo" ? {min:cpcEditForm.dateFrom||undefined} : {max:cpcEditForm.dateTo||undefined})}
      style={{width:140,padding:"6px 8px",borderRadius:6,border:`1px solid ${C.blue}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
  );
  const cpcAmtInput = () => (
    <input type="number" value={cpcEditForm.amount} onChange={e=>setCpcEditForm(f=>({...f,amount:e.target.value}))}
      style={{width:90,padding:"6px 8px",borderRadius:6,border:`1px solid ${C.blue}`,fontSize:13,fontFamily:"inherit",textAlign:"right",boxSizing:"border-box",fontVariantNumeric:"tabular-nums"}} autoFocus/>
  );
  // Inline-edit inputs for an existing platform-contribution entry.
  const coFundDateInput = (key) => (
    <input type="date" value={coFundEditForm[key]} onChange={e=>setCoFundEditForm(f=>({...f,[key]:e.target.value}))}
      onClick={openDatePicker} onFocus={openDatePicker}
      {...(key==="dateTo" ? {min:coFundEditForm.dateFrom||undefined} : {max:coFundEditForm.dateTo||undefined})}
      style={{width:140,padding:"6px 8px",borderRadius:6,border:`1px solid ${C.blue}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
  );
  // % inputs are type="text" inputMode="decimal" (NOT type="number"): a number
  // input hands React "" for "45%" / "12,5" in some browsers (or silently turns
  // "2,5" into 25), so the raw text must reach pctInputToFraction, which accepts
  // a trailing "%" and a decimal comma and reports junk as badNumber.
  const coFundPctInput = (key) => (
    <input type="text" inputMode="decimal" autoComplete="off" value={coFundEditForm[key]} onChange={e=>setCoFundEditForm(f=>({...f,[key]:e.target.value}))}
      aria-label={key==="cofundPct" ? tr("dailylog.cofund.col.cofundPct") : tr("dailylog.cofund.col.discountPct")}
      style={{width:80,padding:"6px 8px",borderRadius:6,border:`1px solid ${C.blue}`,fontSize:13,fontFamily:"inherit",textAlign:"right",boxSizing:"border-box",fontVariantNumeric:"tabular-nums"}}/>
  );
  // Owner's phrasing: the discount split as a share of the ORDER VALUE —
  // platform = discount% × co-fund%, you = discount% × (1 − co-fund%), e.g.
  // 50% / 45% -> "Platform 22.5% · You 27.5%". Up to 2 decimals, trailing zeros
  // trimmed. `strict` (the live editor) also requires the owner's co-fund range,
  // so it never previews a value Save would reject. null -> "—".
  const coFundSplit = (d, c, strict=false) => {
    if(!(typeof d==="number" && d>0 && d<=1)) return null;
    if(!(typeof c==="number" && (strict ? (c>=COFUND_PCT_MIN && c<=COFUND_PCT_MAX) : (c>0 && c<=1)))) return null;
    return { platform: d*c, you: d*(1-c) };
  };
  const pctOfOrder = x => String(+(x*100).toFixed(2));
  const coFundSplitText = (d, c, strict=false) => {
    const s = coFundSplit(d, c, strict);
    return s ? `${tr("dailylog.cofund.split.platform")} ${pctOfOrder(s.platform)}% · ${tr("dailylog.cofund.split.you")} ${pctOfOrder(s.you)}%` : "—";
  };

  // ── Sortable + filterable: CPC allocations table ─────────────────────
  const cpcSortCols = useMemo(()=>([
    {key:"platform", label:"Channel",    type:"text",   value:a=>a.platform,  align:"left"},
    {key:"from",     label:"From",        type:"date",   value:a=>a.dateFrom,  align:"right"},
    {key:"to",       label:"To",          type:"date",   value:a=>a.dateTo,    align:"right"},
    {key:"cpc",      label:"CPC spend",   type:"number", value:a=>a.cpc,       align:"right"},
    {key:"vatCpc",   label:"VAT on CPC",  type:"number", value:a=>(a.cpc||0)*(ws?.vatRate??0), align:"right"},
  ]),[ws]);
  const cpcTable = useTableSortFilter(cpcRows, cpcSortCols);
  // ── Sortable + filterable: platform-contribution table ───────────────
  const coFundSortCols = useMemo(()=>([
    {key:"platform",    label:"Channel",           type:"text",   value:a=>a.platform,    align:"left"},
    {key:"from",        label:"From",              type:"date",   value:a=>a.dateFrom,    align:"right"},
    {key:"to",          label:"To",                type:"date",   value:a=>a.dateTo,      align:"right"},
    {key:"discountPct", label:"Discount %",        type:"number", value:a=>a.discountPct, align:"right"},
    {key:"cofundPct",   label:"Co-fund %",         type:"number", value:a=>a.cofundPct,   align:"right"},
    // Sorts by the platform's share of the order value (discount × co-fund).
    {key:"split",       label:"Split of order value", type:"number", value:a=>a.discountPct*a.cofundPct, align:"right"},
    {key:"amount",      label:"Platform contrib.", type:"number", value:a=>a.amount,      align:"right"},
  ]),[]);
  const coFundTable = useTableSortFilter(coFundRows, coFundSortCols);
  // Live worked example under the add form (owner's £100 example, in the
  // workspace currency). Hidden until both % are valid — the co-fund % inside
  // the owner's COFUND_PCT_MIN..COFUND_PCT_MAX range, exactly what Save accepts —
  // and at a 100% discount (a £0 net is read as "no discount" app-wide, so the
  // example would mislead).
  const coFundHint = (()=>{
    const d = pctInputToFraction(coFundForm.discountPct), c = pctInputToFraction(coFundForm.cofundPct);
    if(!(d>0 && d<1) || !(c>=COFUND_PCT_MIN && c<=COFUND_PCT_MAX)) return null;
    const customer = +(100*(1-d)).toFixed(2);
    const cf = coFundBreakdown(100, customer, {id:"hint", discountPct:d, cofundPct:c});
    return `${tr("dailylog.cofund.hint.title")} ${f(100)}: ${tr("dailylog.cofund.hint.customerPays")} ${f(customer)} · ${tr("dailylog.cofund.hint.platformFunds")} ${f(cf.amount)} · ${tr("dailylog.cofund.hint.youFund")} ${f(100-customer-cf.amount)} · ${tr("dailylog.cofund.hint.feesOn")} ${f(customer+cf.amount)}`;
  })();

  return (
    <div className="nv-page" style={{padding:24}}>
      <SectionHdr title={tr("dailylog.page.title")} sub={`${ws?.name||tr("dailylog.page.workspaceFallback")} · ${filterLabel} · ${rows.length} ${tr("dailylog.records")} · ${currency}`}/>

      {/* P&L breakdown — period totals for the whole Daily Log page (the shared
          date/platform filters live in the top toolbar, App.js). */}
      {/* Info tooltip: the totals use the FULL period CPC ad-spend (from the CPC
          allocations), INCLUDING days with no sales entry — so the total can
          differ from summing the visible per-row CPC. Display-only; no figures
          are changed. The amount is concatenated (tr() keys are static). */}
      {/* Co-fund entries could not be read this load (db.js coFundLoadFailed):
          the figures below leave out the platform's share — say so. */}
      {coFundLoadFailed && (
        <div role="alert" style={{background:C.yellow10,border:`1px solid ${C.yellow}44`,borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12.5,color:"#7a4f00",lineHeight:1.5}}>
          {tr("dailylog.cofund.loadFailed")}
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,letterSpacing:"-.1px"}}>{tr("dailylog.pnl.title")}</div>
        <InfoTip
          text={
            tr("dailylog.pnl.infoTitle") +
            ((t.cpcTopUp||0) > 0
              ? ` ${tr("dailylog.pnl.infoTopUpPrefix")}${f(t.cpcTopUp)}${tr("dailylog.pnl.infoTopUpSuffix")}`
              : "")
            + ((t.coFund||0) > 0
              ? ` ${tr("dailylog.cofund.pnlInfoPrefix")}${f(t.coFund)}${tr("dailylog.cofund.pnlInfoSuffix")}`
              : "")
          }
        />
      </div>
      <div className="nv-grid-md-3 nv-grid-2-sm" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:20}}>
        {[[f(t.gross),tr("dailylog.stat.gross"),C.text,C.blue,null],[f(t.net),tr("dailylog.stat.netReceivable"),C.blue,C.blue,<InfoTip text={tr("dailylog.stat.info.netRecvCard")}/>],[f(t.vat),tr("dailylog.stat.vat"),C.red,C.red,<InfoTip text={tr("dailylog.totals.info.vat")}/>],[f(t.cogs),tr("dailylog.stat.cogs"),C.muted,C.muted,null],[f(t.profit),tr("dailylog.stat.trueProfit"),posc(t.profit),posc(t.profit),<InfoTip text={tr("dailylog.totals.info.trueProfit")} align="right"/>]].map(([v,l,c,a,tip])=>(<StatCard key={l} label={l} value={v} color={c} accent={a} tip={tip}/>))}
      </div>

      {/* Tab switch (Daily log ⇄ CPC spend ⇄ Platform contribution) — only the
          toggled-to table shows. The toolbar (App.js) hosts the "+ Add log entry" /
          "+ Add CPC spend" / "+ Add platform contribution" buttons + Export, which
          switch this tab. The strip wraps so three tabs never scroll a phone sideways. */}
      <div style={{display:"inline-flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
        {[["log",tr("dailylog.section.title")],["cpc",tr("dailylog.cpc.col.spend")],["cofund",tr("dailylog.cofund.tab")]].map(([k,label])=>(
          <button key={k} onClick={()=>setTab(k)}
            style={{padding:"8px 16px",borderRadius:9,border:`1.5px solid ${tab===k?C.blue:C.border}`,background:tab===k?C.blue10:"#fff",color:tab===k?C.blueDark:C.muted,cursor:"pointer",fontSize:13,fontWeight:tab===k?700:600,fontFamily:"inherit",letterSpacing:"-.1px",transition:"all .15s"}}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Daily log section ─────────────────────────────────────────── */}
      {tab==="log" && (<>
      {/* FIRST VALUE — guided first entry (no logs yet in this workspace) */}
      {isFirstRun && !adding && !firstResult && channels.length>0 && (
        <Card sx={{marginBottom:20,border:`2px solid ${C.green}`,background:"#f0fdf4"}}>
          <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6,letterSpacing:"-.2px"}}>{tr("dailylog.first.title")}</div>
          <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:14,maxWidth:620}}>{tr("dailylog.first.body")}</div>
          <button onClick={()=>{ setForm(f=>({...f, channelId: chId||channels[0]?.id||"", date: yesterdayYmd()})); setErr(""); setAdding(true); }}
            style={{padding:"10px 18px",borderRadius:8,border:"none",background:C.green,color:"#fff",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",letterSpacing:"-.1px"}}>
            {tr("dailylog.first.cta")}
          </button>
        </Card>
      )}
      {firstResult && (()=>{
        // Discounts (gross − net) are the operator's own choice; what the
        // platform TOOK is commission + card fees + VAT on those fees. Keep
        // the two apart so a heavy-discount day doesn't read as a 95% fee.
        const discount = Math.max(0, +(firstResult.gross - firstResult.net).toFixed(2));
        const fees = +((firstResult.commissionAmt||0) + (firstResult.cardFees||0) + (firstResult.totalFeeVat||0)).toFixed(2);
        const coFund = firstResult.coFund || 0;
        const base = coFund > 0 ? firstResult.effectiveNet : (firstResult.net > 0 ? firstResult.net : firstResult.gross);
        const keptPct = base>0 ? Math.round((firstResult.netReceivable/base)*100) : 0;
        return (
          <Card sx={{marginBottom:20,border:`2px solid ${C.blue}`,background:"#f6f8ff"}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:8,letterSpacing:"-.2px"}}>{tr("dailylog.first.resultTitle")}</div>
            <div style={{fontSize:14,color:C.text,lineHeight:1.7}}>
              {tr("dailylog.first.resultOn")} <b>{firstResult.date}</b>, <b>{firstResult.platform}</b> {tr("dailylog.first.resultSales")} <b>{f(firstResult.gross)}</b>{discount>0 ? <> ({f(firstResult.net)} {tr("dailylog.first.resultAfterDiscounts")})</> : null}.{coFund>0 ? <>{" "}{tr("dailylog.cofund.firstResultPrefix")}<b>{f(coFund)}</b>{tr("dailylog.cofund.firstResultSuffix")}</> : null}
              {" "}{firstResult.platform} {tr("dailylog.first.resultTook")} <b style={{color:C.red}}>{f(fees)}</b> {tr("dailylog.first.resultFeesLabel")}.
              {" "}{tr("dailylog.first.resultKept")} <b style={{color:C.green}}>{f(firstResult.netReceivable)}</b> ({keptPct}% {coFund>0 ? <>{tr("dailylog.cofund.firstResultOfTrueNet")} {f(firstResult.effectiveNet)}</> : tr("dailylog.first.resultOfNet")}).
            </div>
            <div style={{fontSize:12,color:C.muted,marginTop:8,lineHeight:1.6}}>{tr(fees > 0 ? "dailylog.first.resultHint" : "dailylog.first.resultHintNoRates")}</div>
            <button onClick={()=>setFirstResult(null)} style={{marginTop:12,padding:"7px 14px",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.text,fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{tr("dailylog.first.dismiss")}</button>
          </Card>
        );
      })()}
      {/* Add log entry form */}
      {adding&&(
        <Card sx={{marginBottom:20,border:`2px solid ${C.blue}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:600,color:C.text}}>{tr("dailylog.form.newEntry")}</div>
            <button onClick={()=>setAdding(false)} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{tr("dailylog.btn.cancel")}</button>
          </div>
          {!!err&&<div style={{padding:"8px 12px",background:C.red10,borderRadius:6,marginBottom:12,fontSize:12,color:C.red}}>{err}</div>}
          <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.form.platform")}</label>
              <select value={form.channelId} onChange={e=>setForm({...form,channelId:e.target.value})}
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,color:C.text,background:"#fafafa",fontFamily:"inherit"}}>
                {channels.map(c=>(<option key={c.id} value={c.id}>{c.platform}</option>))}
              </select>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.form.date")}</label>
              <input value={form.date} onChange={e=>setForm({...form,date:e.target.value})} onClick={openDatePicker} onFocus={openDatePicker} type="date" max={new Date().toISOString().split("T")[0]}
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.form.grossSales")} ({sym.trim()})</label>
              <input value={form.gross} onChange={e=>setForm({...form,gross:e.target.value})} placeholder={tr("dailylog.form.grossPlaceholder")} type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.form.netSales")} ({sym.trim()})</label>
              <input value={form.net} onChange={e=>setForm({...form,net:e.target.value})} placeholder={form.gross || tr("dailylog.form.netPlaceholder")} type="number"
                style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.form.kot")}</label>
            <input value={form.kot} onChange={e=>setForm({...form,kot:e.target.value})} placeholder={tr("dailylog.form.kotPlaceholder")}
              style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
          </div>
          <button onClick={saveEntry} disabled={saving}
            style={{padding:"10px 22px",borderRadius:7,border:"none",background:saving?C.muted:C.green,color:"#fff",cursor:saving?"not-allowed":"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>
            {saving?tr("dailylog.btn.saving"):tr("dailylog.btn.saveEntry")}
          </button>
        </Card>
      )}

      <div style={{border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",background:"#fff",boxShadow:C.s2}}>
        {/* Reference-table styling: solid brand-blue header w/ white text,
            subtle zebra, generous row height, no vertical borders.
            Sticky header + sticky first column, tabular-nums. */}
        <div className="nv-table-scroll" style={{overflowX:"auto",overflowY:"auto",maxHeight:"66vh",WebkitOverflowScrolling:"touch"}}>
          <table style={{borderCollapse:"separate",borderSpacing:0,fontSize:13.5,minWidth:1480,width:"100%",fontVariantNumeric:"tabular-nums"}}>
            <thead>
              <tr>{COLS.map((c,ci)=>{
                const isText = c.h==="Channel"||c.h==="Date"||c.h==="KOT";
                const stickyLeft = c.h==="Channel";
                const thStyle = {
                  padding:"16px 18px",
                  fontWeight:600,color:"#fff",whiteSpace:"nowrap",
                  fontSize:11,letterSpacing:.3,
                  position:"sticky",top:0,zIndex:stickyLeft?4:2,
                  background:C.blue,
                  ...(stickyLeft?{left:0,zIndex:5}:{}),
                };
                // Sortable/filterable header for data columns; the action
                // column (no sort key) stays a plain header. Header bar is dark
                // brand-blue, so the sort arrow/placeholder use light tints.
                if(c.sk){
                  return (
                    <SortableTh key={c.h||ci} col={sortColByHeader[c.h]} table={logTable}
                      thStyle={thStyle} align={isText?"left":"right"}
                      arrowColor="rgba(255,255,255,.85)"
                      inputStyle={{background:"rgba(255,255,255,.16)",border:"1px solid rgba(255,255,255,.3)",color:"#fff"}}>
                      {c.label}
                    </SortableTh>
                  );
                }
                return (
                  <th key={c.h||ci} style={{...thStyle,textAlign:isText?"left":"right"}}>{c.label}</th>
                );
              })}</tr>
            </thead>
            <tbody>
              {rows.length===0 && (
                <tr><td colSpan={COLS.length} style={{padding:0}}><EmptyState icon="rows" title={tr("dailylog.empty.title")} message={tr("dailylog.empty.message")} /></td></tr>
              )}
              {rows.length>0 && logTable.rows.length===0 && (
                <tr><td colSpan={COLS.length} style={{padding:"28px 18px",textAlign:"center",color:C.muted,fontSize:13}}>{tr("dailylog.noFilterMatch")}</td></tr>
              )}
              {logTable.rows.map((r,i)=>{
                // A fed row never turns its cells into inputs.
                const isEditing = editingId === r.id && !isFedRow(r);
                const zebra = i%2===1 ? C.blue5 : "#fff";   // subtle lavender tint on alt rows
                const baseBg = isEditing ? C.blue10 : zebra;
                return (
                <tr key={r.id}
                  style={{background:baseBg,transition:"background .1s"}}
                  onMouseEnter={e=>{if(!isEditing)e.currentTarget.style.background=C.blue10;}}
                  onMouseLeave={e=>{if(!isEditing)e.currentTarget.style.background=zebra;}}>
                  {COLS.map((c,j)=>{
                    const isText = c.h==="Channel"||c.h==="Date"||c.h==="KOT";
                    const stickyLeft = c.h==="Channel";
                    const content = (isEditing && c.editKey)
                      ? editCellInput(c.editKey, c.editType)
                      : c.fn(r);
                    return (
                      <td key={j} style={{
                        padding:"15px 18px",
                        borderBottom:`1px solid ${C.border2}`,
                        whiteSpace:"nowrap",
                        textAlign:isText?"left":"right",
                        fontSize:13.5,color:C.text,
                        ...(stickyLeft?{position:"sticky",left:0,zIndex:1,background:baseBg}:{}),
                      }}>{content}</td>
                    );
                  })}
                </tr>
                );
              })}
              {/* Totals row — derived from COLS by header so it can NEVER drift
                  out of alignment when columns are added/removed/reordered. */}
              {rows.length>0 && (
              <tr style={{background:C.bg2}}>
                {COLS.map((c,j)=>{
                  const isText = c.h==="Channel"||c.h==="Date"||c.h==="KOT";
                  const stickyLeft = c.h==="Channel";
                  const totalFor = {
                    "Channel":      <span style={{color:C.text,fontWeight:700,fontSize:10.5,textTransform:"uppercase",letterSpacing:.6}}>{tr("dailylog.totals")}</span>,
                    [`Gross (${sym.trim()})`]: <span style={{color:C.text,fontWeight:700}}>{f(t.gross)}</span>,
                    "Net Ticket":   <span style={{color:C.text,fontWeight:700}}>{f(t.netTicket)}</span>,
                    "Discount":     <span style={{color:C.sub}}>{f(Math.max(0,t.gross - t.netTicket))}</span>,
                    "Platform contrib.": <span style={{color:C.sub}}>{f(t.coFund)}</span>,
                    "Commission":   <span style={{color:C.sub}}>{f(t.commission)}</span>,
                    "VAT/Comm":     <span style={{color:C.sub}}>{f(t.vatComm)}</span>,
                    "Card Fees":    <span style={{color:C.sub}}>{f(t.card)}</span>,
                    "VAT/Card":     <span style={{color:C.sub}}>{f(t.vatCard)}</span>,
                    "Net Recv.":    <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}><span style={{color:C.text,fontWeight:700}}>{f(t.net)}</span><InfoTip text={tr("dailylog.totals.info.netRecv")}/></span>,
                    "VAT":          <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}><span style={{color:C.sub}}>{f(t.vat)}</span><InfoTip text={tr("dailylog.totals.info.vat")}/></span>,
                    "COGS":         <span style={{color:C.sub}}>{f(t.cogs)}</span>,
                    "True Profit":  <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}><span style={{fontWeight:800,color:(t.profit<0?C.red:C.text)}}>{f(t.profit)}</span><InfoTip text={tr("dailylog.totals.info.trueProfit")} align="right"/></span>,
                  }[c.h];
                  return <td key={j} style={{
                    padding:"15px 18px",fontWeight:700,whiteSpace:"nowrap",
                    textAlign:isText?"left":"right",
                    borderTop:`2px solid ${C.border}`,fontSize:13.5,
                    ...(stickyLeft?{position:"sticky",left:0,zIndex:1,background:C.bg2}:{}),
                  }}>{totalFor || ""}</td>;
                })}
              </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>)}{/* end Daily log section */}

      {/* ── CPC / campaign spend section (date-range allocations) — own tab ─ */}
      {tab==="cpc" && (<>
        <div style={{marginBottom:cpcAdding?16:(cpcRows.length?16:0)}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{tr("dailylog.cpc.title")}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500}}>{tr("dailylog.cpc.subtitle")}</div>
        </div>

        {/* Add / edit form */}
        {cpcAdding && (
          <div style={{border:`2px solid ${C.blue}`,borderRadius:10,padding:16,marginBottom:cpcRows.length?16:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{tr("dailylog.cpc.newSpend")}</div>
              <button onClick={()=>{setCpcAdding(false);setCpcErr("");}} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{tr("dailylog.btn.cancel")}</button>
            </div>
            {!!cpcErr&&<div style={{padding:"8px 12px",background:C.red10,borderRadius:6,marginBottom:12,fontSize:12,color:C.red}}>{cpcErr}</div>}
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cpc.platform")}</label>
                <select value={cpcForm.channelId} onChange={e=>setCpcForm({...cpcForm,channelId:e.target.value})}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,color:C.text,background:"#fafafa",fontFamily:"inherit"}}>
                  {channels.map(c=>(<option key={c.id} value={c.id}>{c.platform}</option>))}
                </select>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cpc.amount")} ({sym.trim()})</label>
                <input value={cpcForm.amount} onChange={e=>setCpcForm({...cpcForm,amount:e.target.value})} placeholder={tr("dailylog.cpc.amountPlaceholder")} type="number"
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cpc.fromDate")}</label>
                <input value={cpcForm.dateFrom} onChange={e=>setCpcForm({...cpcForm,dateFrom:e.target.value})} {...openPickerProps} type="date" max={cpcForm.dateTo||undefined}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cpc.toDate")}</label>
                <input value={cpcForm.dateTo} onChange={e=>setCpcForm({...cpcForm,dateTo:e.target.value})} {...openPickerProps} type="date" min={cpcForm.dateFrom||undefined}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
            </div>
            <button onClick={saveCpc} disabled={cpcSaving}
              style={{padding:"10px 22px",borderRadius:7,border:"none",background:cpcSaving?C.muted:C.green,color:"#fff",cursor:cpcSaving?"not-allowed":"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>
              {cpcSaving?tr("dailylog.btn.saving"):tr("dailylog.btn.save")}
            </button>
          </div>
        )}

        {/* Allocations table */}
        {cpcRows.length===0 ? (
          !cpcAdding && <EmptyState icon="tag" title={tr("dailylog.cpc.empty.title")} message={tr("dailylog.cpc.empty.message")} />
        ) : (
          <div style={{border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",background:"#fff"}}>
            <div className="nv-table-scroll" style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"separate",borderSpacing:0,fontSize:13.5,width:"100%",fontVariantNumeric:"tabular-nums"}}>
                <thead>
                  <tr>
                    {["Channel","From","To","CPC spend","VAT on CPC",""].map((h,ci)=>{
                      const isText = h==="Channel";
                      const thStyle = {padding:"14px 18px",fontWeight:600,color:"#fff",whiteSpace:"nowrap",fontSize:11,letterSpacing:.3,background:C.blue};
                      // `h` stays the STABLE logic key (sort-column lookup below);
                      // `label` is the translated text rendered in the header cell.
                      const label = {"Channel":tr("dailylog.cpc.col.channel"),"From":tr("dailylog.cpc.col.from"),"To":tr("dailylog.cpc.col.to"),"CPC spend":tr("dailylog.cpc.col.spend"),"VAT on CPC":tr("dailylog.cpc.col.vat")}[h] || "";
                      // Map header label → reusable sort column (the trailing
                      // actions header "" has no match → plain <th>).
                      const sc = {"Channel":cpcSortCols[0],"From":cpcSortCols[1],"To":cpcSortCols[2],"CPC spend":cpcSortCols[3],"VAT on CPC":cpcSortCols[4]}[h];
                      if(sc){
                        return (
                          <SortableTh key={h||ci} col={sc} table={cpcTable} thStyle={thStyle} align={isText?"left":"right"}
                            arrowColor="rgba(255,255,255,.85)"
                            inputStyle={{background:"rgba(255,255,255,.16)",border:"1px solid rgba(255,255,255,.3)",color:"#fff"}}>
                            {label}
                          </SortableTh>
                        );
                      }
                      return (
                        <th key={h||ci} style={{...thStyle,textAlign:isText?"left":"right"}}>{label}</th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {cpcTable.rows.length===0 && (
                    <tr><td colSpan={6} style={{padding:"24px 18px",textAlign:"center",color:C.muted,fontSize:13}}>{tr("dailylog.noFilterMatch")}</td></tr>
                  )}
                  {cpcTable.rows.map((a,i)=>{
                    const zebra = i%2===1 ? C.blue5 : "#fff";
                    const isCpcEditing = cpcEditingId === a.id;
                    const shownCpc = isCpcEditing ? (parseFloat(cpcEditForm.amount)||0) : (a.cpc||0);
                    const vat = shownCpc * (ws?.vatRate ?? 0);
                    const baseBg = isCpcEditing ? C.blue10 : zebra;
                    const cell = {padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,whiteSpace:"nowrap",textAlign:"right"};
                    return (
                      <tr key={a.id}
                        style={{background:baseBg,transition:"background .1s"}}
                        onMouseEnter={e=>{if(!isCpcEditing)e.currentTarget.style.background=C.blue10;}}
                        onMouseLeave={e=>{if(!isCpcEditing)e.currentTarget.style.background=zebra;}}>
                        <td style={{...cell,textAlign:"left"}}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                            <span style={{width:9,height:9,borderRadius:5,background:a.color,flexShrink:0}}/>
                            <span style={{fontWeight:600,color:C.text}}>{a.platform}</span>
                          </span>
                        </td>
                        <td style={{...cell,color:C.text,fontWeight:500}}>{isCpcEditing ? cpcDateInput("dateFrom") : fmtDateCell(new Date(a.dateFrom+"T00:00:00"))}</td>
                        <td style={{...cell,color:C.text,fontWeight:500}}>{isCpcEditing ? cpcDateInput("dateTo") : fmtDateCell(new Date(a.dateTo+"T00:00:00"))}</td>
                        <td style={{...cell,color:cNum,fontWeight:600}}>{isCpcEditing ? cpcAmtInput() : f(a.cpc)}</td>
                        <td style={{...cell,color:cMuted}}>{f(vat)}</td>
                        <td style={cell}>
                          <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                            {isCpcEditing ? (
                              <>
                                <button onClick={()=>saveCpcEdit(a)} disabled={cpcEditSaving}
                                  style={{padding:"5px 10px",borderRadius:6,border:"none",background:C.blue,color:"#fff",cursor:cpcEditSaving?"wait":"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>
                                  {cpcEditSaving?tr("dailylog.btn.savingEllipsis"):tr("dailylog.btn.save")}
                                </button>
                                <button onClick={cancelCpcEdit} disabled={cpcEditSaving}
                                  style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:500}}>
                                  {tr("dailylog.btn.cancel")}
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={()=>startCpcEdit(a)} title={tr("dailylog.cpc.editTitle")}
                                  style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                                  onMouseEnter={e=>{e.currentTarget.style.background=C.bg3;e.currentTarget.style.color=C.text;e.currentTarget.style.borderColor=C.muted2;}}
                                  onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button onClick={()=>deleteCpc(a.id)} title={tr("dailylog.cpc.deleteTitle")}
                                  style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                                  onMouseEnter={e=>{e.currentTarget.style.background=C.red10;e.currentTarget.style.color=C.red;e.currentTarget.style.borderColor="#f8c9c9";}}
                                  onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{background:C.bg2}}>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"left"}}><span style={{color:C.text,fontWeight:700,fontSize:10.5,textTransform:"uppercase",letterSpacing:.6}}>{tr("dailylog.totals")}</span></td>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}><span style={{color:C.text,fontWeight:700}}>{f(cpcTotals.cpc)}</span></td>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}><span style={{color:C.sub}}>{f(cpcTotals.vat)}</span></td>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,whiteSpace:"nowrap"}}/>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>)}{/* end CPC section */}

      {/* ── Platform contribution (co-funded discounts) — own tab ─────────
          Rendered ONLY on this tab, so e2e/daily-log.spec.js's unscoped
          input[type="date"] locator on the log tab never sees these inputs. */}
      {tab==="cofund" && (<>
        <div style={{marginBottom:(coFundLoadFailed||coFundAdding)?16:(coFundRows.length?16:0)}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text,letterSpacing:"-.2px"}}>{tr("dailylog.cofund.title")}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:4,fontWeight:500,maxWidth:760,lineHeight:1.5}}>{tr("dailylog.cofund.subtitle")}</div>
        </div>

        {/* Entries could not be read (db.js coFundLoadFailed): the list is NOT
            known to be empty, so no add form (a re-entry could duplicate an
            entry that exists) and no misleading "none yet" empty state. The
            toolbar "+ Add platform contribution" lands here too. */}
        {coFundLoadFailed ? (
          <div role="status" style={{background:C.yellow10,border:`1px solid ${C.yellow}44`,borderRadius:10,padding:"14px 18px",fontSize:13,color:"#7a4f00",lineHeight:1.5}}>
            {tr("dailylog.cofund.loadFailedTab")}
          </div>
        ) : (<>
        {coFundAdding && (
          <div style={{border:`2px solid ${C.blue}`,borderRadius:10,padding:16,marginBottom:coFundRows.length?16:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{tr("dailylog.cofund.newEntry")}</div>
              <button onClick={()=>{setCoFundAdding(false);setCoFundErr("");}} style={{padding:"4px 12px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{tr("dailylog.btn.cancel")}</button>
            </div>
            {!!coFundErr&&<div style={{padding:"8px 12px",background:C.red10,borderRadius:6,marginBottom:12,fontSize:12,color:C.red}}>{coFundErr}</div>}
            {channels.length===0 ? (
              <div style={{fontSize:12.5,color:C.muted}}>{tr("dailylog.cofund.noChannels")}</div>
            ) : (<>
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cofund.platform")}</label>
                <select value={coFundFormChannelId} onChange={e=>setCoFundForm({...coFundForm,channelId:e.target.value})} aria-label={tr("dailylog.cofund.platform")}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,color:C.text,background:"#fafafa",fontFamily:"inherit"}}>
                  {coFundFormChannels.map(c=>(<option key={c.id} value={c.id}>{c.platform}</option>))}
                </select>
                {coFundScopeCh && channels.length>1 && (
                  <div style={{fontSize:11,color:C.muted,marginTop:5,lineHeight:1.4}}>{tr("dailylog.cofund.platformScopedHint")}</div>
                )}
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cofund.discountPct")}</label>
                <input value={coFundForm.discountPct} onChange={e=>setCoFundForm({...coFundForm,discountPct:e.target.value})} placeholder={tr("dailylog.cofund.discountPlaceholder")} type="text" inputMode="decimal" autoComplete="off" aria-label={tr("dailylog.cofund.discountPct")}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cofund.fromDate")}</label>
                <input value={coFundForm.dateFrom} onChange={e=>setCoFundForm({...coFundForm,dateFrom:e.target.value})} {...openPickerProps} type="date" max={coFundForm.dateTo||undefined}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cofund.toDate")}</label>
                <input value={coFundForm.dateTo} onChange={e=>setCoFundForm({...coFundForm,dateTo:e.target.value})} {...openPickerProps} type="date" min={coFundForm.dateFrom||undefined}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div className="nv-grid-1-sm" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <label style={{fontSize:10,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7,display:"block",marginBottom:5}}>{tr("dailylog.cofund.cofundPct")}</label>
                <input value={coFundForm.cofundPct} onChange={e=>setCoFundForm({...coFundForm,cofundPct:e.target.value})} placeholder={`${Math.round(COFUND_PCT_MIN*100)}–${Math.round(COFUND_PCT_MAX*100)}`} type="text" inputMode="decimal" autoComplete="off" aria-label={tr("dailylog.cofund.cofundPct")}
                  style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div style={{alignSelf:"end",fontSize:12,color:C.muted,lineHeight:1.5}}>{coFundHint}</div>
            </div>
            <button onClick={saveCoFund} disabled={coFundSaving}
              style={{padding:"10px 22px",borderRadius:7,border:"none",background:coFundSaving?C.muted:C.green,color:"#fff",cursor:coFundSaving?"not-allowed":"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>
              {coFundSaving?tr("dailylog.btn.saving"):tr("dailylog.btn.save")}
            </button>
            </>)}
          </div>
        )}

        {/* Entries table */}
        {coFundRows.length===0 ? (
          !coFundAdding && <EmptyState icon="tag" title={tr("dailylog.cofund.empty.title")} message={tr("dailylog.cofund.empty.message")} />
        ) : (
          <div style={{border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",background:"#fff"}}>
            <div className="nv-table-scroll" style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"separate",borderSpacing:0,fontSize:13.5,width:"100%",fontVariantNumeric:"tabular-nums"}}>
                <thead>
                  <tr>
                    {["Channel","From","To","Discount %","Co-fund %","Split of order value","Platform contrib.",""].map((h,ci)=>{
                      const isText = h==="Channel";
                      const thStyle = {padding:"14px 18px",fontWeight:600,color:"#fff",whiteSpace:"nowrap",fontSize:11,letterSpacing:.3,background:C.blue};
                      // `h` stays the STABLE logic key (sort-column lookup below);
                      // `label` is the translated text rendered in the header cell.
                      const label = {"Channel":tr("dailylog.cofund.col.channel"),"From":tr("dailylog.cofund.col.from"),"To":tr("dailylog.cofund.col.to"),"Discount %":tr("dailylog.cofund.col.discountPct"),"Co-fund %":tr("dailylog.cofund.col.cofundPct"),"Split of order value":tr("dailylog.cofund.col.split"),"Platform contrib.":tr("dailylog.cofund.col.amount")}[h] || "";
                      // Map header label → reusable sort column (the trailing
                      // actions header "" has no match → plain <th>).
                      const sc = {"Channel":coFundSortCols[0],"From":coFundSortCols[1],"To":coFundSortCols[2],"Discount %":coFundSortCols[3],"Co-fund %":coFundSortCols[4],"Split of order value":coFundSortCols[5],"Platform contrib.":coFundSortCols[6]}[h];
                      if(sc){
                        return (
                          <SortableTh key={h||ci} col={sc} table={coFundTable} thStyle={thStyle} align={isText?"left":"right"}
                            arrowColor="rgba(255,255,255,.85)"
                            inputStyle={{background:"rgba(255,255,255,.16)",border:"1px solid rgba(255,255,255,.3)",color:"#fff"}}>
                            {label}
                          </SortableTh>
                        );
                      }
                      return (<th key={h||ci} style={{...thStyle,textAlign:isText?"left":"right"}}>{label}</th>);
                    })}
                  </tr>
                </thead>
                <tbody>
                  {coFundTable.rows.length===0 && (
                    <tr><td colSpan={8} style={{padding:"24px 18px",textAlign:"center",color:C.muted,fontSize:13}}>{tr("dailylog.noFilterMatch")}</td></tr>
                  )}
                  {coFundTable.rows.map((a,i)=>{
                    const zebra = i%2===1 ? C.blue5 : "#fff";
                    const isCoFundEditing = coFundEditingId === a.id;
                    const baseBg = isCoFundEditing ? C.blue10 : zebra;
                    const cell = {padding:"14px 18px",borderBottom:`1px solid ${C.border2}`,whiteSpace:"nowrap",textAlign:"right"};
                    const pctText = v => { const s = fractionToPctInput(v); return s==="" ? "—" : `${s}%`; };
                    return (
                      <tr key={a.id}
                        style={{background:baseBg,transition:"background .1s"}}
                        onMouseEnter={e=>{if(!isCoFundEditing)e.currentTarget.style.background=C.blue10;}}
                        onMouseLeave={e=>{if(!isCoFundEditing)e.currentTarget.style.background=zebra;}}>
                        <td style={{...cell,textAlign:"left"}}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:8}}>
                            <span style={{width:9,height:9,borderRadius:5,background:a.color,flexShrink:0}}/>
                            <span style={{fontWeight:600,color:C.text}}>{a.platform}</span>
                          </span>
                        </td>
                        <td style={{...cell,color:C.text,fontWeight:500}}>{isCoFundEditing ? coFundDateInput("dateFrom") : fmtDateCell(new Date(a.dateFrom+"T00:00:00"))}</td>
                        <td style={{...cell,color:C.text,fontWeight:500}}>{isCoFundEditing ? coFundDateInput("dateTo") : fmtDateCell(new Date(a.dateTo+"T00:00:00"))}</td>
                        <td style={{...cell,color:C.text}}>{isCoFundEditing ? coFundPctInput("discountPct") : pctText(a.discountPct)}</td>
                        <td style={{...cell,color:C.text}}>{isCoFundEditing ? coFundPctInput("cofundPct") : pctText(a.cofundPct)}</td>
                        {/* Live while editing (from the typed %), stored values otherwise. */}
                        <td style={{...cell,color:C.sub}}>{isCoFundEditing
                          ? coFundSplitText(pctInputToFraction(coFundEditForm.discountPct), pctInputToFraction(coFundEditForm.cofundPct), true)
                          : coFundSplitText(a.discountPct, a.cofundPct)}</td>
                        <td style={{...cell,color:cNum,fontWeight:600}}>{f(a.amount)}</td>
                        <td style={cell}>
                          <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                            {isCoFundEditing ? (
                              <>
                                <button onClick={()=>saveCoFundEdit(a)} disabled={coFundEditSaving}
                                  style={{padding:"5px 10px",borderRadius:6,border:"none",background:C.blue,color:"#fff",cursor:coFundEditSaving?"wait":"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>
                                  {coFundEditSaving?tr("dailylog.btn.savingEllipsis"):tr("dailylog.btn.save")}
                                </button>
                                <button onClick={cancelCoFundEdit} disabled={coFundEditSaving}
                                  style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:500}}>
                                  {tr("dailylog.btn.cancel")}
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={()=>startCoFundEdit(a)} title={tr("dailylog.cofund.editTitle")}
                                  style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                                  onMouseEnter={e=>{e.currentTarget.style.background=C.bg3;e.currentTarget.style.color=C.text;e.currentTarget.style.borderColor=C.muted2;}}
                                  onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button onClick={()=>deleteCoFund(a.id)} title={tr("dailylog.cofund.deleteTitle")}
                                  style={{width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:7,border:`1px solid ${C.border}`,background:"#fff",color:C.muted,cursor:"pointer",transition:"all .12s",padding:0}}
                                  onMouseEnter={e=>{e.currentTarget.style.background=C.red10;e.currentTarget.style.color=C.red;e.currentTarget.style.borderColor="#f8c9c9";}}
                                  onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.color=C.muted;e.currentTarget.style.borderColor=C.border;}}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{background:C.bg2}}>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"left"}}><span style={{color:C.text,fontWeight:700,fontSize:10.5,textTransform:"uppercase",letterSpacing:.6}}>{tr("dailylog.totals")}</span></td>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}/>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,fontWeight:700,whiteSpace:"nowrap",textAlign:"right"}}><span style={{color:C.text,fontWeight:700}}>{f(coFundTotals.amount)}</span></td>
                    <td style={{padding:"14px 18px",borderTop:`2px solid ${C.border}`,whiteSpace:"nowrap"}}/>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}{/* end coFundLoadFailed ? … : (form + table) */}
      </>)}{/* end Platform contribution section */}

      {/* CSV import / export — deterministic, round-trips by ID. (Daily-log tab only) */}
      {tab==="log" && (
      <div style={{marginTop:20}}>
        {userPlan==="pro"||userPlan==="superadmin"
          ? <CsvImportExport
              title={tr("dailylog.csv.title")}
              subtitle={tr("dailylog.csv.subtitle")}
              templateHeaders={DAILY_CSV_HEADERS}
              buildExportRows={buildDailyExportRows}
              exportFilename={`netrev-dailylog-${(ws?.name||"export").replace(/\s+/g,"-")}.csv`}
              onImport={importDaily}
              noun={tr("dailylog.csv.noun")}
            />
          : <div style={{background:C.yellow10,border:`1px solid ${C.yellow}44`,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12,fontSize:13}}>
              <span>📤</span>
              <span style={{color:"#7a4f00"}}>{tr("dailylog.csv.proFeaturePrefix")}<strong>{tr("dailylog.csv.proFeatureBold")}</strong>{tr("dailylog.csv.proFeatureDot")}<span style={{color:C.blue,cursor:"pointer",fontWeight:600}}>{tr("dailylog.csv.upgrade")}</span></span>
            </div>
        }
      </div>
      )}
    </div>
  );
}
